/**
 * ecransEdition.ts, la TRANSFORMATION d'un fichier d'ecrans.
 *
 * POURQUOI CE FICHIER EXISTE, et c'est la meme raison que `champsEdition.ts` :
 * c'est le second endroit du studio qui REECRIT un fichier du depot, donc une
 * erreur ici ne casse pas un affichage, elle casse l'ecran de quelqu'un. Il est
 * PURE (des entrees, une sortie, aucun etat React), donc il se teste avec
 * `node --test`, et `packages/shell/tests/ecrans.test.mjs` le couvre cas par
 * cas.
 *
 * CE QU'IL NE FAIT PAS : il ne valide pas. La validation est faite par le pont,
 * qui ecrit puis rejoue le garde-fou de la CI et DEFAIT si le fichier est
 * refuse. Recopier le schema ici en ferait une seconde source de verite, et
 * c'est elle qui mentirait en premier. Consequence assumee : une transformation
 * fidele peut produire un fichier invalide (demonter la vue d'arrivee, vider
 * une carte). Le refus arrive alors avec sa raison, et rien n'est ecrit.
 *
 * LE VOCABULAIRE D'UN CHANGEMENT est un ID, et rien d'autre. L'ecran n'a aucune
 * table de correspondance a tenir : il pose `ecran:<rang>:<quoi>...` dans le
 * brouillon, ce module le relit. Les formes, toutes prefixees `ecran:` :
 *
 *   ecran:<r>:vue:<vueId>                oui | ''      la vue est montee
 *   ecran:<r>:arrivee                    <vueId>       la vue d'arrivee
 *   ecran:<r>:porte-arrivee              <porteId>     la porte d'arrivee (vide = premiere)
 *   ecran:<r>:custom:<vueId>             <texte>       le libelle d'une vue custom
 *   ecran:<r>:role:<vueId>:<role>        '' | <champ> | fn:<nom>
 *   ecran:<r>:roles:<vueId>:<role>       <champs separes par des virgules>
 *   ecran:<r>:ecrit:<vueId>:<role>       oui | ''      le role s'ecrit
 *   ecran:<r>:cols:<vueId>               <champs separes par des virgules>
 *   ecran:<r>:mot:<vueId>:titre|statut   <texte>       le mot de cette vue
 *   ecran:<r>:total:<champ>              '' | somme | moyenne | compte
 *   ecran:<r>:triable:<champ>            '' | oui | non
 *   ecran:<r>:carte:<vueId>:<i>:titre    <texte>
 *   ecran:<r>:carte:<vueId>:<i>:disposition  demi | pleine | titre-pleine
 *   ecran:<r>:carte:<vueId>:<i>:placement    principal | droite   (carte de champs)
 *   ecran:<r>:carte:<vueId>:<i>:vue      core.liste | core.tableau  (carte liee)
 *   ecran:<r>:carte:<vueId>:<i>:champs   <champs separes par des virgules>
 *   ecran:<r>:carte:<vueId>:<i>:rang     <position, a partir de 1>
 *   ecran:<r>:carte:<vueId>:<i>:retirer  oui           la section s'en va
 *   ecran:<r>:carte-neuve:<vueId>:<uid>:titre|champs
 *   ecran:<r>:porte-vb:<porteId>:<role|roles|ecrit|cols|mot|carte>:<vueId>:...
 *   ecran:<r>:porte-vb:<porteId>:carte-neuve:<vueId>:<uid>:titre|champs
 *   ecran:<r>:porte-vues:<porteId>:vue:<vueId>  oui | ''  vue de cette porte
 *   ecran:<r>:porte-vues:<porteId>:arrivee       <vueId>   arrivee de cette porte
 *   ecran:<r>:tri:role:<role>            oui | ''      la clef est au catalogue
 *   ecran:<r>:tri:champ:<champ>          oui | ''      idem, sur une colonne
 *   ecran:<r>:trimot:role:<role>         <texte>       le mot d'un role trie
 *   ecran:<r>:verbe:<archive|...>        oui | ''      ce geste de fiche est offert
 *   ecran:<r>:porte:<porte>:label        <texte>       le nom de la porte
 *   ecran:<r>:porte:<porte>:famille      <famille>     la famille du jeu montre
 *   ecran:<r>:porte:<porte>:defauts      <champ=valeur, champ=valeur> ce qu'une fiche NEUVE porte
 *   ecran:<r>:porte:<porte>:placeholder  <texte>       le mot de la recherche
 *   ecran:<r>:porte:<porte>:tranche      <condition JSON> '' = tout le jeu
 *   ecran:<r>:porte:<porte>:rang         <position dans le menu, a partir de 1>
 *   ecran:<r>:porte:<porte>:vide-titre        <texte>  ce qu'on lit sur un jeu vide
 *   ecran:<r>:porte:<porte>:vide-indication   <texte>
 *   ecran:<r>:porte:<porte>:introuvable-titre      <texte>
 *   ecran:<r>:porte:<porte>:introuvable-indication <texte>
 *   ecran:<r>:porte:<porte>:modeles      oui | ''      les modeles de fiches
 *   ecran:<r>:porte:<porte>:recurrences  oui | ''      les plans recurrents
 *   ecran:<r>:porte:<porte>:modifierDansLeTableau  oui | ''  le tableau s'edite en place
 *   ecran:<r>:porte:<porte>:tranchable   oui | ''      '' ECRIT false (absent = oui)
 *   ecran:<r>:porte:<porte>:navigation   oui | ''      '' ECRIT false (absent = oui) : hors du menu
 *   ecran:<r>:porte:<porte>:auClic       fiche | <lien> | vue:<vueDeFiche> | porte:<porte> | porte:<app>/<porte>
 *                                        au clic sur une fiche ('' = fiche)
 *   ecran:<r>:porte:<porte>:auClicVue    '' | <vueId>  sa vue d'arrivee ('' = celle de la porte liee)
 *   ecran:<r>:porte:<porte>:etapes:<n>      '' | <champ>  l'en-tete de rang <n> (a partir de 0) de
 *                                           « En tête de fiche » ('' = il s'en va ; un rang au-dela
 *                                           de la liste en ajoute un)
 *   ecran:<r>:porte:<porte>:etapes:<n>:mode flux | selection
 *   ecran:<r>:porte:<porte>:etapes       ANCIENNE clef, sans rang : le rang 0
 *   ecran:<r>:porte:<porte>:etapesMode   ANCIENNE clef, sans rang : le mode du rang 0
 *   ecran:<r>:porte:<porte>:vuesFiche    <vues separees par des virgules>
 *                                        les vues de fiche montrees en plus de Fiche ('' = Fiche seule ;
 *                                        une vue Canvas n'y est que citee ; le defaut, les vues HTML
 *                                        du jeu sans vue Canvas, = la clef part)
 *   ecran:<r>:vue-html:<id>:retirer      oui           la vue HTML s'en va
 *   ecran:<r>:porte:<porte>:retirer      oui           la porte s'en va, cascade comprise
 *   ecran:<r>:filtres:<porte>:parchamp   '' | <champ>  un filtre par valeur de
 *   ecran:<r>:filtreautre:<porte>:<id>   <texte>       le mot d'un filtre nomme
 *   ecran:<r>:filtreautre:<porte>:<id>:retirer  oui    le filtre nomme s'en va
 *   ecran:<r>:favori:<porte>:<id>:label  <texte>
 *   ecran:<r>:favori:<porte>:<id>:filtres  <ids separes par des virgules>
 *   ecran:<r>:favori:<porte>:<id>:retirer  oui
 *   ecran:<r>:favori-neuf:<porte>:<uid>:label|filtres
 *   ecran:<r>:action:<porte>:<id>:label  <texte>
 *   ecran:<r>:action:<porte>:<id>:variant  '' | primary | secondary
 *   ecran:<r>:action:<porte>:<id>:retirer  oui
 *   ecran:<r>:stat:<porte>:<id>:label|calcul|champ|valeur
 *   ecran:<r>:stat:<porte>:<id>:retirer  oui
 *   ecran:<r>:stat-neuve:<porte>:<uid>:label|calcul|champ|valeur
 *
 * CE QUI S'AJOUTE VOYAGE A PART : une PORTE neuve arrive par `portesNeuves`,
 * comme une carte neuve par `cartesNeuves`. Son id peut etre fixe des sa saisie
 * pour cibler ses reglages dans le meme brouillon. Favoris et stats passent par
 * le brouillon, sous un `<uid>` que le moteur n'ecrit jamais (l'id final se
 * derive du libelle, comme partout ailleurs dans le Studio).
 *
 * `<champ>` est le SEUL argument qui puisse contenir le separateur, parce qu'un
 * champ natif s'ecrit `socle:reference` : il se RECOLLE a la lecture. Les autres
 * (un id de vue, un nom de role, un rang) n'en portent jamais.
 *
 * LA VIRGULE N'EST QUE LE TRANSPORT d'une liste de valeurs FERMEES : des clefs
 * de champs et des ids de vue, qui ne peuvent pas en contenir (leurs formes sont
 * verifiees par le schema et par la CI). Aucune de ces listes ne se TAPE : elles
 * se cochent.
 */
import { conditionSchema, enTetesDeLaPorte, fautesDesEtapes, vuesDeFicheDeLaPorte, type EnTeteDeFiche } from '@taki/data-model';
import { CALCULS_DE_STAT, ROLES_TRIABLES } from './data.js';
import { VUES } from './familles.mjs';
/* `kebab` EST PARTAGE AVEC LE MOTEUR DE BRIEF, comme `clefDeChamp` pour un champ :
   il derive l'id d'une porte, d'un favori et d'une stat quand le
   moteur d'app ECRIT une declaration (`idProjete`, `brief/derive.mjs`). Un
   second derivateur ici donnerait deux ids pour un meme libelle selon qui
   ecrit. Ce n'est PAS `clefDeChamp` : sa sortie est en camelCase (`tarifJour`),
   que les trois schemas refusent (`^[a-z][a-z0-9-]*$`). */
import { kebab, vueParDefautDuClic } from './identifiants.mjs';
import type {
  ActionLue,
  BindingLu,
  CarteDeChampsLue,
  CarteDeclareeLue,
  ColonneLue,
  DeclarationLue,
  EcranLu,
  EcransLus,
  EntreeDeCarteLue,
  EtatDePorteLu,
  FavoriLu,
  NiveauDeTriLu,
  OptionDeTriLue,
  PorteDeclareeLue,
  SourceLue,
  StatLue,
} from './modele.js';
import {
  estCarteDeChampsLue,
  estCarteLieeLue,
  estTotalDeCarteLu,
  nomsDeChampsDeCarteLue,
} from './modele.js';

/** Le prefixe de tout id qui vise le fichier d'ecrans. */
export const PREFIXE_ECRAN = 'ecran:';

/** Les neuf roles du contrat des vues. Au-dela, c'est un role propre (`extra`). */
const ROLES_DU_CONTRAT = new Set([
  'title',
  'subtitle',
  'avatar',
  'status',
  'badges',
  'groupKey',
  'lane',
  'dateStart',
  'dateEnd',
]);

/** Ce que porte la valeur d'un role quand elle nomme une fonction. */
const MARQUE_FN = 'fn:';

/** La clef d'un niveau ou d'une option de tri, dans le vocabulaire du socle. */
const clefDeTri = (niveau: { champ?: string; role?: string }): string =>
  niveau.role === undefined ? `champ:${niveau.champ ?? ''}` : `role:${niveau.role}`;

/** Les quoi qui visent UNE porte, et dont `args[0]` est son id. */
const QUOI_DE_PORTE = [
  'porte',
  'filtres',
  'filtreautre',
  'favori',
  'favori-neuf',
  'action',
  'stat',
  'stat-neuve',
  'porte-vb',
  'porte-vues',
];

/** La valeur qui demande un RETRAIT. Toute autre valeur ne retire rien. */
const OUI = 'oui';

/** Les calculs de stat que le moteur accepte d'ecrire, par leur clef. */
const CALCULS_CONNUS = new Set(CALCULS_DE_STAT.map((calcul) => calcul.value));

/**
 * LE REPLI D'UN ID DE PORTE, quand le libelle ne donne rien d'utilisable.
 *
 * EXPORTE AVEC `idDepuisLabel`, ET POUR LA MEME RAISON : l'ecran derive le meme
 * id que ce moteur pour savoir sur quelle porte s'ouvrir apres l'ecriture. Un
 * repli recopie a l'autre bout donnerait deux ids pour le meme libelle des
 * qu'on en changerait ici.
 */
export const REPLI_ID_PORTE = 'porte';

/** Une liste transportee : les valeurs non vides, dans l'ordre tape. */
const liste = (valeur: string): string[] =>
  valeur
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');

/**
 * LES VALEURS PAR DEFAUT, LUES DE LA LIGNE `champ=valeur, champ=valeur`.
 *
 * Une paire sans clef, sans `=` ou sans valeur est ECARTEE, jamais devinee :
 * une clef seule ne dit pas ce qu'on veut poser, et une valeur vide ferait
 * naitre une case renseignee-mais-vide.
 */
export function defautsSaisis(valeur: string): Record<string, string | number | boolean> {
  const sortie: Record<string, string | number | boolean> = {};
  for (const paire of liste(valeur)) {
    const coupe = paire.indexOf('=');
    if (coupe <= 0) continue;
    const clef = paire.slice(0, coupe).trim();
    const brut = paire.slice(coupe + 1).trim();
    if (clef === '' || brut === '') continue;
    if (brut === 'oui' || brut === 'true') sortie[clef] = true;
    else if (brut === 'non' || brut === 'false') sortie[clef] = false;
    else if (/^-?\d+(\.\d+)?$/.test(brut)) sortie[clef] = Number(brut);
    else sortie[clef] = brut;
  }
  return sortie;
}

/**
 * UN ID DERIVE D'UN LIBELLE, RENDU UNIQUE DANS SON VOISINAGE.
 *
 * MEME GESTE QUE `idProjete` DU MOTEUR D'APP (`brief/derive.mjs`), et pour la
 * meme raison : cet id VOYAGE (l'adresse d'une porte, la preference de poste
 * d'un favori), donc il vient du LIBELLE, la seule chose que quelqu'un ait
 * tapee. Les trois schemas exigent la meme forme, `^[a-z][a-z0-9-]*$`.
 *
 * `repli` SERT QUAND LE LIBELLE NE DONNE RIEN D'UTILISABLE (des chiffres, des
 * symboles, une autre ecriture) : on ne fabrique pas un id vide, et on ne
 * refuse pas non plus un libelle que le fondateur a bien tape.
 *
 * ELLE EST EXPORTEE POUR QUE L'ECRAN SACHE OU IL VIENT D'ECRIRE. Apres avoir
 * enregistre une porte neuve, le rail doit s'ouvrir SUR elle : il lui faut donc
 * son id, celui que ce moteur vient de deriver. Le recalculer autrement (une
 * seconde fonction, un « slug » maison) donnerait deux ids pour un libelle le
 * jour ou l'un des deux changerait d'avis, et le rail ouvrirait une porte qui
 * n'existe pas. Une regle, un endroit, deux appelants.
 */
export function idDepuisLabel(label: string, pris: ReadonlySet<string>, repli: string): string {
  let nu = kebab(label);
  if (!/^[a-z]/.test(nu)) nu = nu === '' ? repli : `${repli}-${nu}`;
  let unique = nu;
  let rang = 2;
  while (pris.has(unique)) {
    unique = `${nu}-${String(rang)}`;
    rang += 1;
  }
  return unique;
}

/**
 * CE QU'UNE PORTE EN ATTENTE APPORTE : son nom, le jeu et l'id stabilise par
 * l'ecran. Ses autres reglages ciblent cet id dans le brouillon avant l'ecriture.
 */
export interface PorteNeuve {
  /** L'id de sa ligne dans le brouillon, tel que l'ecran l'a pose. */
  id: string;
  ecran: number;
  label: string;
  /** La clef du jeu montre (`famille::type`), telle que le fichier de champs la nomme. */
  jeu: string;
  /** Id stabilise pendant la saisie ; absent, derive du libelle. */
  porteId?: string;
  /** Detail propre a un jeu que nulle porte de cet ecran ne montre encore. */
  montage?: { titre: string; colonnes: ColonneLue[] };
}

/** Ce qu'une carte en attente apporte : son titre et ses champs. */
export interface CarteNeuve {
  /** L'id de la ligne de titre, tel que l'ecran l'a pose. */
  id: string;
  ecran: number;
  vue: string;
  /** Absente pour le detail commun de l'ecran ; presente pour celui d'une porte. */
  porteId?: string;
  titre: string;
  champs: string[];
}

/** Ce qu'un brouillon du studio demande de changer dans le fichier d'ecrans. */
export interface EditionEcrans {
  /** Le fichier tel qu'il a ete lu. */
  ecrans: EcransLus;
  /** Les lignes du brouillon qui visent ce fichier : id -> valeur. */
  changements: readonly (readonly [string, string])[];
}

/** Un changement, decoupe. */
interface Change {
  ecran: number;
  quoi: string;
  args: string[];
  valeur: string;
}

/**
 * Le decoupage d'un id. Rend `null` sur tout ce qui ne vise pas ce fichier :
 * un id mal forme n'est pas une exception, c'est une ligne qui n'est pas pour
 * nous.
 */
function decouper(id: string, valeur: string): Change | null {
  if (!id.startsWith(PREFIXE_ECRAN)) return null;
  const morceaux = id.slice(PREFIXE_ECRAN.length).split(':');
  const rang = Number(morceaux[0]);
  if (!Number.isInteger(rang) || rang < 0 || morceaux.length < 2) return null;
  return { ecran: rang, quoi: morceaux[1]!, args: morceaux.slice(2), valeur };
}

/**
 * LA PORTE QU'UN ID D'EDITION VISE, ou `null` quand il vise l'ECRAN entier.
 *
 * ELLE EST EXPORTEE POUR LE RAIL : une entree qui porte du brouillon prend sa
 * pastille, et il faut donc savoir a QUI appartient une ligne modifiee. Le
 * decoupage est celui du moteur, pas un second ; le deduire d'un libelle
 * affiche ou d'un morceau d'id recoupe a la main est exactement la faute qui a
 * deja envoye tout champ neuf dans le mauvais jeu, en silence.
 *
 * `null` SUR UNE LIGNE D'ECRAN, ET C'EST UN FAIT : une vue montee, un geste de
 * fiche ou une clef de tri valent pour TOUTES les portes de l'ecran. L'appelant
 * en fait ce qu'il veut (les marquer toutes) ; ce module ne choisit pas une
 * porte au hasard.
 */
export function porteVisee(id: string): string | null {
  const change = decouper(id, '');
  if (change === null) return null;
  if (!QUOI_DE_PORTE.includes(change.quoi)) return null;
  const porte = change.args[0];
  return porte === undefined || porte === '' ? null : porte;
}

/**
 * LE FICHIER D'ECRANS APRES LE BROUILLON. L'entree n'est jamais modifiee.
 *
 * LES CARTES EN ATTENTE arrivent a part : elles n'existent dans aucun fichier,
 * elles n'ont donc pas de rang a viser. Une carte sans titre ou sans champ
 * n'est PAS ecrite : le schema la refuserait, et l'ecran le dit sous la ligne.
 */
export function ecransSuivants(
  edition: EditionEcrans,
  cartesNeuves: readonly CarteNeuve[] = [],
  portesNeuves: readonly PorteNeuve[] = [],
  champsDeclares?: DeclarationLue,
): EcransLus {
  const changes = edition.changements
    .map(([id, valeur]) => decouper(id, valeur))
    .filter((c): c is Change => c !== null);

  const suivants: EcransLus = {
    ...edition.ecrans,
    ecrans: edition.ecrans.ecrans.map((ecran, rang) =>
      ecranSuivant(
        ecran,
        changes.filter((c) => c.ecran === rang),
        cartesNeuves.filter((c) => c.ecran === rang && c.titre.trim() !== '' && c.champs.length > 0),
        /* UNE PORTE SANS NOM OU SANS JEU N'EST PAS ECRITE, exactement comme une
           carte sans titre : le schema exige les deux, et une porte a moitie
           remplie ferait refuser TOUTE l'ecriture, pour tout le reste du
           brouillon. L'ecran le dit sous la ligne. */
        portesNeuves.filter(
          (p) => p.ecran === rang && p.label.trim() !== '' && p.jeu.trim() !== '',
        ),
        champsDeclares,
      ),
    ),
  };
  /* LE CHAMP DES ETAPES, JUGE CONTRE LE FICHIER DE CHAMPS quand on l'a : un
     champ declare retire du jeu, ou devenu multiple, est refuse avec sa raison
     (`fautesDesEtapes`, la regle du montage). Le serveur du Studio embarque ce
     moteur : l'instance fautive ne s'enregistre plus pour disparaitre ensuite. */
  if (champsDeclares !== undefined) {
    const [faute] = fautesDesEtapes({ champs: champsDeclares, ecrans: suivants });
    if (faute !== undefined) throw new Error(`« En tête de fiche » : ${faute}`);
  }
  return suivants;
}

/** Un ecran apres le brouillon. */
function ecranSuivant(
  ecran: EcranLu,
  changes: readonly Change[],
  cartesNeuves: readonly CarteNeuve[],
  portesNeuves: readonly PorteNeuve[] = [],
  champsDeclares?: DeclarationLue,
): EcranLu {
  if (changes.length === 0 && cartesNeuves.length === 0 && portesNeuves.length === 0) return ecran;
  let suite: EcranLu = { ...ecran };

  /* 1. LE CATALOGUE DE COLONNES. Il est a l'ECRAN, pas a la vue : un agregat
     pose ici vaut pour toutes les vues qui reprennent cette colonne, et c'est
     exactement ce qu'on veut (deux totaux pour une colonne divergeraient). */
  const colonnes = changes.filter((c) => c.quoi === 'total' || c.quoi === 'triable');
  if (colonnes.length > 0 && suite.colonnes !== undefined) {
    suite = { ...suite, colonnes: suite.colonnes.map((col) => colonneSuivante(col, colonnes)) };
  }

  /* 2. LE DETAIL COMMUN PAR VUE. Les cartes d'une porte n'y entrent pas. */
  const vbCommun = detailsSuivants(suite.vb, changes, cartesNeuves.filter((c) => c.porteId === undefined));
  if (vbCommun !== suite.vb) suite = { ...suite, vb: vbCommun };

  /* 3. LES LIBELLES DES VUES CUSTOM. */
  const libelles = changes.filter((c) => c.quoi === 'custom');
  if (libelles.length > 0 && suite.vuesCustom !== undefined) {
    suite = {
      ...suite,
      vuesCustom: suite.vuesCustom.map((vue) => {
        const change = libelles.find((c) => c.args[0] === vue.id);
        return change === undefined || change.valeur.trim() === ''
          ? vue
          : { ...vue, label: change.valeur.trim() };
      }),
    };
  }

  /*
   * 3bis. LE RETRAIT D'UNE VUE HTML (`ecran:<r>:vue-html:<id>:retirer`, oui),
   * deposee par le MCP : elle sort de `vuesHtml`, et des portes qui la
   * citaient (leur liste de vues de fiche, leur clic). Le Studio ne la
   * retouche jamais autrement ; une nouvelle version passe par `vue_publier`.
   */
  const retraitsHtml = new Set(
    changes.filter((c) => c.quoi === 'vue-html' && c.args[1] === 'retirer' && c.valeur === OUI).map((c) => c.args[0] ?? ''),
  );
  if (retraitsHtml.size > 0 && suite.vuesHtml !== undefined) {
    const inconnue = [...retraitsHtml].find((id) => !(suite.vuesHtml ?? []).some((v) => v.id === id));
    if (inconnue !== undefined) throw new Error(`La vue HTML « ${inconnue} » n'existe pas sur cet écran.`);
    const gardees = suite.vuesHtml.filter((v) => !retraitsHtml.has(v.id));
    const { vuesHtml: _parties, ...sansHtml } = suite;
    suite = {
      ...(gardees.length > 0 ? { ...sansHtml, vuesHtml: gardees } : sansHtml),
      portes: suite.portes.map((porte) => {
        let suivante = porte;
        if (porte.vuesFiche !== undefined && porte.vuesFiche.some((v) => retraitsHtml.has(v))) {
          suivante = { ...suivante, vuesFiche: porte.vuesFiche.filter((v) => !retraitsHtml.has(v)) };
        }
        const clic = porte.auClic;
        if (clic !== undefined && 'vueDeFiche' in clic && retraitsHtml.has(clic.vueDeFiche)) suivante = sansClef(suivante, 'auClic');
        return suivante;
      }),
    };
  }

  /*
   * 4. LES VUES MONTEES, EN DERNIER, et l'ordre compte : une vue demontee perd
   * son detail. Le schema refuse un `vb` qui decrit une vue non montee, et une
   * carte d'identite custom que rien ne monte ; les elaguer ici est ce qui
   * evite d'ecrire un fichier que le pont defera aussitot.
   *
   * CE QUE CA COUTE, ET C'EST DIT A L'ECRAN : demonter puis remonter une vue ne
   * ramene pas son detail. Il a ete efface avec elle.
   */
  const montage = changes.filter((c) => c.quoi === 'vue');
  if (montage.length > 0) {
    const montees = suite.vues.filter(
      (vue) => (montage.find((c) => c.args[0] === vue)?.valeur ?? 'oui') !== '',
    );
    for (const c of montage) {
      const vue = c.args[0];
      if (vue !== undefined && c.valeur === 'oui' && !montees.includes(vue)) montees.push(vue);
    }
    suite = { ...suite, vues: montees };
    const vb: Record<string, BindingLu | undefined> = {};
    for (const [vue, detail] of Object.entries(suite.vb)) {
      if (montees.includes(vue)) vb[vue] = detail;
    }
    suite = { ...suite, vb };
    if (suite.vuesCustom !== undefined) {
      /* UNE VUE DE FICHE (`niveau: 'record'`) ne se monte pas dans `vues` : le
         montage des vues de collection ne la touche pas. */
      const gardees = suite.vuesCustom.filter((vue) => vue.niveau === 'record' || montees.includes(vue.id));
      if (gardees.length > 0) suite = { ...suite, vuesCustom: gardees };
      else {
        const { vuesCustom: _partie, ...sansCustom } = suite;
        suite = sansCustom;
      }
    }
  }

  /* 5. LA VUE D'ARRIVEE. */
  const arrivee = changes.find((c) => c.quoi === 'arrivee');
  if (arrivee !== undefined && arrivee.valeur.trim() !== '') {
    suite = { ...suite, arrivee: arrivee.valeur.trim() };
  }

  /* 6. LE CATALOGUE DE TRI, declare par l'ECRAN : il vaut pour ses portes. */
  const tri = changes.filter((c) => c.quoi === 'tri' || c.quoi === 'trimot');
  if (tri.length > 0) suite = triSuivant(suite, tri);

  /* 7. LES TROIS GESTES DE FICHE, trois booleens. */
  const verbes = changes.filter((c) => c.quoi === 'verbe');
  if (verbes.length > 0) suite = verbesSuivants(suite, verbes);

  /* 8. CE QUE CHAQUE PORTE DECLARE : ses filtres, ses favoris, ses actions,
     ses stats. `args[0]` est TOUJOURS l'id de la porte visee. */
  const pourPortes = changes.filter((c) => QUOI_DE_PORTE.includes(c.quoi));
  if (pourPortes.length > 0) {
    suite = {
      ...suite,
      portes: suite.portes.map((porte) =>
        porteSuivante(
          porte,
          pourPortes.filter((c) => c.args[0] === porte.id),
        ),
      ),
    };
  }

  /* 8bis. LES PORTES RETIREES, ET TOUT CE QUI LES CITAIT. Apres les reglages :
     regler puis retirer doit rendre le meme fichier que retirer tout court. */
  suite = portesApresRetrait(suite, changes, portesNeuves.length > 0);

  /* 8ter. LES PORTES NEUVES, EN DERNIER : une porte ajoutee dans le meme
     brouillon qu'un retrait ne doit pas se faire elaguer par sa cascade, et son
     id doit etre unique face aux portes qui RESTENT. */
  if (portesNeuves.length > 0) {
    const avantAjout = suite.portes;
    suite = { ...suite, portes: [...suite.portes, ...portesAjoutees(suite, portesNeuves)] };
    const idsAjoutes = new Set(suite.portes.filter((porte) => !avantAjout.includes(porte)).map((porte) => porte.id));
    if (idsAjoutes.size > 0) {
      suite = {
        ...suite,
        portes: suite.portes.map((porte) => idsAjoutes.has(porte.id)
          ? porteSuivante(porte, pourPortes.filter((c) => c.args[0] === porte.id))
          : porte),
      };
    }
    const sansAinee = portesNeuves.filter((neuve) =>
      neuve.montage !== undefined && !avantAjout.some((porte) => clefDeJeu(porte) === neuve.jeu),
    );
    if (sansAinee.length > 0) {
      const vuesAvant = suite.vues;
      const vues = [...vuesAvant];
      for (const vue of ['core.liste', 'core.fiche']) if (!vues.includes(vue)) vues.push(vue);
      const colonnes = [...(suite.colonnes ?? [])];
      const prises = new Set(colonnes.map((colonne) => colonne.champ));
      for (const neuve of sansAinee) {
        for (const colonne of neuve.montage?.colonnes ?? []) {
          if (prises.has(colonne.champ)) continue;
          prises.add(colonne.champ);
          colonnes.push(colonne);
        }
      }
      suite = {
        ...suite,
        vues,
        ...(colonnes.length === 0 ? {} : { colonnes }),
        /* Une vue ajoutee au catalogue commun ne s'impose pas aux anciennes portes. */
        portes: suite.portes.map((porte) =>
          avantAjout.includes(porte) && porte.vues === undefined
            ? { ...porte, vues: [...vuesAvant] }
            : porte,
        ),
      };
    }
  }

  /* 8quater. Le detail d'une porte, apres sa creation : un meme brouillon peut
     monter une porte et regler ses roles, colonnes et cartes avant l'ecriture. */
  const detailsDePorte = changes.filter((c) => c.quoi === 'porte-vb');
  if (detailsDePorte.length > 0 || cartesNeuves.some((c) => c.porteId !== undefined)) {
    suite = {
      ...suite,
      portes: suite.portes.map((porte) => {
        const pourPorte = detailsDePorte
          .filter((c) => c.args[0] === porte.id)
          .map((c): Change => ({ ...c, quoi: c.args[1] ?? '', args: c.args.slice(2) }));
        const cartes = cartesNeuves.filter((c) => c.porteId === porte.id);
        const vb = detailsSuivants(porte.vb ?? suite.vb, pourPorte, cartes);
        return pourPorte.length === 0 && cartes.length === 0 ? porte : { ...porte, vb };
      }),
    };
  }

  /* 8quinquies. Les vues locales restent dans le catalogue de l'ecran. Leur
     arrivee ne peut etre demontee ; une vue demontee perd son detail local. */
  const vuesDePorte = changes.filter((c) => c.quoi === 'porte-vues');
  if (vuesDePorte.length > 0) {
    const vuesAvant = suite.vues;
    const permises = new Set([...VUES.map((vue) => vue.id as string), ...(suite.vuesCustom ?? []).filter((vue) => vue.niveau !== 'record').map((vue) => vue.id)]);
    const nouvellesVues = vuesDePorte
      .filter((c) => c.args[1] === 'vue' && c.valeur === OUI)
      .map((c) => c.args[2])
      .filter((vue): vue is string => vue !== undefined && permises.has(vue) && !vuesAvant.includes(vue));
    if (nouvellesVues.length > 0) {
      suite = {
        ...suite,
        vues: [...vuesAvant, ...new Set(nouvellesVues)],
        /* Le catalogue etait herite par ces portes. Elles gardent leur choix
           pendant qu'une autre porte y ajoute sa propre vue. */
        portes: suite.portes.map((porte) => porte.vues === undefined
          ? { ...porte, vues: [...vuesAvant] }
          : porte),
      };
    }
    suite = {
      ...suite,
      portes: suite.portes.map((porte) => {
        const pourPorte = vuesDePorte.filter((c) => c.args[0] === porte.id);
        return pourPorte.length === 0 ? porte : porteVuesSuivantes(porte, suite, pourPorte);
      }),
    };
  }

  /* LA PLACE DES PORTES DANS LE MENU, apres les portes neuves : « 1 » met une
     porte en premier. Les autres gardent leur ordre relatif. */
  const rangs = changes.filter((c) => c.quoi === 'porte' && c.args[1] === 'rang');
  for (const c of rangs) {
    const position = Number(c.valeur.trim());
    const actuel = suite.portes.findIndex((porte) => porte.id === c.args[0]);
    if (actuel < 0 || !Number.isInteger(position) || position < 1) continue;
    const portes = [...suite.portes];
    const [deplacee] = portes.splice(actuel, 1);
    portes.splice(Math.min(position - 1, portes.length), 0, deplacee!);
    suite = { ...suite, portes };
  }

  const porteArrivee = changes.find((c) => c.quoi === 'porte-arrivee');
  if (porteArrivee !== undefined) {
    const id = porteArrivee.valeur.trim();
    if (id === '') {
      const { porteArrivee: _ancienne, ...sansArrivee } = suite;
      suite = sansArrivee;
    } else {
      suite = { ...suite, porteArrivee: id };
    }
  }

  /*
   * 9. L'ELAGAGE DU TRI, EN DERNIER. Une clef demontee du catalogue ne peut
   * plus etre citee : ni par la pile d'arrivee, ni par un favori. Le schema le
   * refuserait, et l'ecriture serait defaite avec un message que personne ne
   * relie au geste. On la retire donc la ou elle etait citee, et l'ecran l'a
   * annonce sous la case (« la demonter la retire aussi des favoris »).
   */
  if (tri.length > 0) suite = elaguerLeTri(suite);

  if (champsDeclares !== undefined) {
    suite = colonnesCustomAjoutees(suite, changes, cartesNeuves, champsDeclares);
  }

  /* 10. LE CLIC SUR UNE FICHE, apres tout ce qui peut retirer un lien ou
     demonter une vue de la porte liee. */
  suite = clicsElagues(suite, changes, ecran);

  /* 10bis. LES VUES DE FICHE D'UNE PORTE QUI LES COCHE TOUTES retirent leur
     clef : l'absence les montre toutes, y compris celles qu'un `vue_publier`
     ajoutera demain. Seules les portes que ce brouillon regle sont relues. */
  suite = vuesFicheTenues(suite, changes);

  /* 11. HORS NAVIGATION, apres tout ce qui peut retirer un lien, une porte ou
     changer l'arrivee. Une porte dont le brouillon regle la navigation n'est
     pas touchee : ce qu'on demande s'ecrit, ou revient refuse. */
  const navigationDite = new Set(
    changes.filter((c) => c.quoi === 'porte' && c.args[1] === 'navigation').map((c) => c.args[0] ?? ''),
  );
  return navigationsTenues(suite, navigationDite);
}

/**
 * HORS NAVIGATION, TENU : une porte `navigation: false` devenue la porte
 * d'arrivee, ou que plus aucune autre porte ne lie, REVIENT dans le menu (la
 * clef tombe). Le schema la refuserait, et le refus parlerait d'une porte que
 * personne n'a touchee : on la perdrait sinon, sans entree ni menu. Les portes
 * de `reglees` sont laissees telles quelles.
 */
export function navigationsTenues(ecran: EcranLu, reglees: ReadonlySet<string> = new Set()): EcranLu {
  const arrivee = ecran.porteArrivee ?? ecran.portes[0]?.id;
  let touche = false;
  const portes = ecran.portes.map((porte) => {
    if (porte.navigation !== false || reglees.has(porte.id)) return porte;
    const liee = ecran.portes.some(
      (autre) => autre.id !== porte.id && (autre.liens ?? []).some((lien) => lien.porte === porte.id),
    );
    if (porte.id !== arrivee && liee) return porte;
    touche = true;
    return sansClef(porte, 'navigation');
  });
  return touche ? { ...ecran, portes } : ecran;
}

/** Une colonne personnalisée choisie dans le détail devient cataloguée. */
function colonnesCustomAjoutees(
  ecran: EcranLu,
  changes: readonly Change[],
  cartesNeuves: readonly CarteNeuve[],
  champsDeclares: DeclarationLue,
): EcranLu {
  const choisies: { nom: string; porteId?: string }[] = [];
  for (const change of changes) {
    const propre = change.quoi === 'porte-vb'
      ? { quoi: change.args[1], args: change.args.slice(2), porteId: change.args[0] }
      : { quoi: change.quoi, args: change.args, porteId: undefined };
    const colonnes = propre.quoi === 'cols'
      || (propre.quoi === 'carte' && propre.args[2] === 'champs');
    if (!colonnes) continue;
    for (const nom of liste(change.valeur)) choisies.push({ nom, porteId: propre.porteId });
  }
  for (const carte of cartesNeuves) {
    for (const nom of carte.champs) choisies.push({ nom, porteId: carte.porteId });
  }
  if (choisies.length === 0) return ecran;

  const colonnes = [...(ecran.colonnes ?? [])];
  const presentes = new Set(colonnes.map((colonne) => colonne.champ));
  const jeuDAccueil = ecran.portes.find((porte) => porte.id === ecran.porteArrivee)?.jeu
    ?? ecran.portes[0]?.jeu;
  for (const { nom, porteId } of choisies) {
    if (nom.startsWith('socle:') || presentes.has(nom)) continue;
    const portes = ecran.portes.filter((porte) =>
      porteId === undefined ? porte.vb === undefined : porte.id === porteId,
    );
    if (portes.length === 0 || !portes.every((porte) => {
      const jeu = porte.jeu ?? jeuDAccueil;
      return jeu !== undefined && champsDeclares.jeux.some((declare) =>
        declare.family === jeu.family && declare.type === jeu.type
        && declare.champs.some((champ) => champ.key === nom),
      );
    })) continue;
    presentes.add(nom);
    colonnes.push({ champ: nom });
  }
  return colonnes.length === (ecran.colonnes ?? []).length ? ecran : { ...ecran, colonnes };
}

/** Le sous-catalogue de vues d'une porte, et sa vue d'arrivee. */
function porteVuesSuivantes(
  porte: PorteDeclareeLue,
  ecran: EcranLu,
  changes: readonly Change[],
): PorteDeclareeLue {
  const montees = new Set((porte.vues ?? ecran.vues).filter((vue) => ecran.vues.includes(vue)));
  const demandee = changes.find((c) => c.args[1] === 'arrivee')?.valeur.trim();
  const arrivee = demandee !== undefined && ecran.vues.includes(demandee)
    ? demandee : porte.arrivee ?? ecran.arrivee;
  if (ecran.vues.includes(arrivee)) montees.add(arrivee);
  const ajoutees: string[] = [];
  for (const change of changes.filter((c) => c.args[1] === 'vue')) {
    const vue = change.args[2];
    if (vue === undefined || !ecran.vues.includes(vue)) continue;
    if (change.valeur === OUI) {
      if (!montees.has(vue)) ajoutees.push(vue);
      montees.add(vue);
    }
    else if (vue !== arrivee && montees.size > 1) montees.delete(vue);
  }
  const vues = ecran.vues.filter((vue) => montees.has(vue));
  const arriveeGardee = vues.includes(arrivee) ? arrivee : vues[0];
  const vbDePorte = { ...(porte.vb ?? ecran.vb) };
  for (const vue of ajoutees) {
    if (vbDePorte[vue] !== undefined) continue;
    const modele = vbDePorte['core.liste'] ?? vbDePorte['core.fiche']
      ?? Object.values(vbDePorte).find((detail) => detail?.title !== undefined);
    const titre = modele?.title ?? { champ: 'socle:id' };
    const colonnes = vbDePorte['core.fiche']?.colonnes ?? vbDePorte['core.liste']?.colonnes
      ?? (titre.champ !== undefined && ecran.colonnes?.some((colonne) => colonne.champ === titre.champ)
        ? [titre.champ] : undefined);
    vbDePorte[vue] = {
      title: titre,
      ...(vue === 'core.tableau' && colonnes !== undefined ? { colonnes: [...colonnes] } : {}),
    };
  }
  const vb = porte.vb === undefined && ajoutees.length === 0 ? undefined : Object.fromEntries(
    Object.entries(vbDePorte).filter(([vue]) => montees.has(vue)),
  );
  return {
    ...porte,
    vues,
    ...(arriveeGardee === undefined ? {} : { arrivee: arriveeGardee }),
    ...(vb === undefined ? {} : { vb }),
  };
}

/**
 * LE CATALOGUE DE TRI APRES LE BROUILLON.
 *
 * MONTER UNE CLEF DE ROLE LUI ECRIT SON MOT : le schema exige un libelle sur un
 * role (rien d'autre ne connait le mot de « Titre »), et une option sans
 * libelle serait refusee. Le mot vient de `ROLES_TRIABLES`, la meme table que
 * celle qui nomme la case a cocher : deux tables auraient donne deux mots.
 *
 * MONTER UNE CLEF DE CHAMP NE LUI ECRIT RIEN : son libelle vit dans le fichier
 * de champs, ou dans la colonne native qui le declare. L'ecrire ici en ferait le
 * meme fait a deux endroits.
 */
function triSuivant(ecran: EcranLu, changes: readonly Change[]): EcranLu {
  let options: OptionDeTriLue[] = [...(ecran.tri?.options ?? [])];

  for (const c of changes) {
    if (c.quoi !== 'tri') continue;
    /* LE NOM DU CHAMP SE RECOLLE : c'est le seul argument qui puisse contenir
       le separateur, parce qu'un champ natif s'ecrit `socle:reference`. */
    const role = c.args[0] === 'role' ? c.args[1] : undefined;
    const champ = c.args[0] === 'champ' ? c.args.slice(1).join(':') : undefined;
    if (role === undefined && champ === undefined) continue;
    const clef = role === undefined ? `champ:${champ ?? ''}` : `role:${role}`;
    const index = options.findIndex((o) => clefDeTri(o) === clef);
    if (c.valeur === 'oui') {
      if (index >= 0) continue;
      options.push(
        role === undefined
          ? { champ: champ as string }
          : { role, label: ROLES_TRIABLES.find((r) => r.role === role)?.mot ?? role },
      );
    } else if (index >= 0) {
      options.splice(index, 1);
    }
  }

  for (const c of changes) {
    if (c.quoi !== 'trimot' || c.args[0] !== 'role') continue;
    const mot = c.valeur.trim();
    /* Un mot vide n'est PAS ecrit : le schema exige un libelle sur un role, et
       l'effacer ferait refuser tout le fichier. La relecture le redira. */
    if (mot === '') continue;
    options = options.map((o) => (o.role === c.args[1] ? { ...o, label: mot } : o));
  }

  if (options.length === 0) {
    const { tri: _retire, ...sansTri } = ecran;
    return sansTri;
  }
  return { ...ecran, tri: { ...(ecran.tri ?? {}), options } };
}

/** Les piles de tri, ramenees aux clefs que le catalogue porte encore. */
function elaguerLeTri(ecran: EcranLu): EcranLu {
  const clefs = new Set((ecran.tri?.options ?? []).map(clefDeTri));
  const garder = (pile: readonly NiveauDeTriLu[]): NiveauDeTriLu[] =>
    pile.filter((niveau) => clefs.has(clefDeTri(niveau)));

  let suite = ecran;
  if (ecran.tri?.defaut !== undefined) {
    const defaut = garder(ecran.tri.defaut);
    suite = {
      ...suite,
      tri:
        defaut.length === 0
          ? { options: ecran.tri.options }
          : { options: ecran.tri.options, defaut },
    };
  }
  return {
    ...suite,
    portes: suite.portes.map((porte) =>
      porte.favoris === undefined
        ? porte
        : { ...porte, favoris: porte.favoris.map((f) => ({ ...f, tri: garder(f.tri) })) },
    ),
  };
}

/**
 * LES TROIS GESTES DE FICHE APRES LE BROUILLON.
 *
 * « Non » s'ecrit en RETIRANT la clef : `archive: false` et l'absence disent la
 * meme chose au socle (l'entree du menu est grisee avec sa raison dans les deux
 * cas), et deux ecritures pour un sens finissent par diverger.
 */
function verbesSuivants(ecran: EcranLu, changes: readonly Change[]): EcranLu {
  const verbes: Record<string, boolean> = { ...(ecran.verbes ?? {}) };
  for (const c of changes) {
    const clef = c.args[0];
    if (clef !== 'archive' && clef !== 'unarchive' && clef !== 'remove') continue;
    if (c.valeur === 'oui') verbes[clef] = true;
    else delete verbes[clef];
  }
  if (Object.keys(verbes).length === 0) {
    const { verbes: _retire, ...sansVerbes } = ecran;
    return sansVerbes;
  }
  return { ...ecran, verbes };
}

/** Une porte apres le brouillon : ses filtres, ses favoris, ses actions, ses stats. */
function porteSuivante(porte: PorteDeclareeLue, changes: readonly Change[]): PorteDeclareeLue {
  if (changes.length === 0) return porte;
  let suite: PorteDeclareeLue = { ...porte };

  /*
   * LE NOM DE LA PORTE. C'est le libelle du menu, et il se renomme : depuis que
   * la porte est declaree dans le fichier d'ecrans, c'est une ecriture de
   * chaine, pas une modification de code.
   *
   * L'ID, LUI, NE BOUGE PAS. Il voyage dans l'adresse (le parametre `s`), il est
   * cite par les favoris et par le garde-fou de canvas : le changer casserait
   * des liens que ce fichier ne connait pas tous.
   *
   * UN NOM VIDE EST REFUSE, il n'est pas ecrit : une entree de menu sans mot ne
   * se clique pas, et le schema l'exige de toute facon.
   */
  const renomme = changes.find((c) => c.quoi === 'porte' && c.args[1] === 'label');
  if (renomme !== undefined && renomme.valeur.trim() !== '') {
    suite = { ...suite, label: renomme.valeur.trim() };
  }

  /* LA TRANCHE : une condition du socle, en JSON. Vide la retire ; illisible,
     elle est refusee avec sa raison plutot qu'ecrite a moitie. */
  const tranche = changes.find((c) => c.quoi === 'porte' && c.args[1] === 'tranche');
  if (tranche !== undefined) {
    const texte = tranche.valeur.trim();
    if (texte === '') suite = sansClef(suite, 'tranche');
    else {
      let brute: unknown;
      try {
        brute = JSON.parse(texte);
      } catch {
        throw new Error(`La tranche de la porte « ${porte.label} » n'est pas une condition JSON lisible.`);
      }
      const lue = conditionSchema.safeParse(brute);
      if (!lue.success) throw new Error(`La tranche de la porte « ${porte.label} » n'est pas une condition du socle.`);
      suite = { ...suite, tranche: lue.data };
    }
  }

  /*
   * LA FAMILLE DU JEU MONTRE, quand elle etait un TROU NOMME.
   *
   * `porte.jeu` POINTE un jeu de `taki.fields.json` : les deux fichiers portent
   * la MEME valeur, et le garde-fou les recoupe. Ce changement-la ne s'ecrit
   * donc jamais seul, il part avec l'ecriture du fichier de champs dans le meme
   * tour (`/__taki/studio/declarations`) ; ecrit d'un cote seulement, il serait
   * refuse et defait.
   *
   * SUR UNE FAMILLE REELLE, LE STUDIO NE PROPOSE PAS CE GESTE : `sdk.<famille>`
   * est ecrit dans le code de l'app, et la changer ici ne changerait pas ce
   * code. Cette branche ne fait donc que ce que l'ecran lui demande, sur une
   * porte qui n'a pas de jeu, elle ne fabrique rien.
   */
  const famille = changes.find((c) => c.quoi === 'porte' && c.args[1] === 'famille');
  if (famille !== undefined && famille.valeur.trim() !== '' && suite.jeu !== undefined) {
    suite = { ...suite, jeu: { ...suite.jeu, family: famille.valeur.trim() } };
  }

  /*
   * CE QU'UNE FICHE NEUVE PORTE DEJA (`creation.defauts`).
   *
   * LA FORME SAISIE EST `champ=valeur, champ=valeur`, et c'est la seule ligne
   * du studio qui TAPE des clefs de champ : les autres se cochent. La virgule
   * separe les paires, le premier `=` separe la clef de la valeur (une valeur
   * peut en contenir un, une clef jamais : le schema fixe leur forme).
   *
   * LA VALEUR EST TYPEE COMME DANS LE BRIEF : un nombre quand elle en est un,
   * `oui`/`non` quand c'est une case, une chaine sinon. Le schema du socle
   * n'accepte que ces trois formes.
   *
   * VIDE RETIRE `creation` : « pas de defaut » et « defaut vide » sont deux
   * choses differentes, et la seconde ferait naitre une fiche avec une case
   * renseignee-mais-vide.
   */
  const defauts = changes.find((c) => c.quoi === 'porte' && c.args[1] === 'defauts');
  if (defauts !== undefined) {
    const lues = defautsSaisis(defauts.valeur);
    if (Object.keys(lues).length === 0) {
      const { creation: _retire, ...sansCreation } = suite;
      suite = sansCreation as PorteDeclareeLue;
    } else {
      suite = { ...suite, creation: { defauts: lues } };
    }
  }

  /* LES MOTS DE LA PORTE : la recherche, ses deux etats, ses deux booleens. */
  suite = motsDePorte(suite, changes);

  /* LE CATALOGUE DE FILTRES. Vider le champ retire la clef ; retirer la
     derniere chose que `filtres` portait retire `filtres` avec elle. */
  const parChamp = changes.find((c) => c.quoi === 'filtres' && c.args[1] === 'parchamp');
  if (parChamp !== undefined) {
    const filtres = { ...(suite.filtres ?? {}) };
    if (parChamp.valeur.trim() === '') delete filtres.parChamp;
    else filtres.parChamp = parChamp.valeur.trim();
    suite =
      filtres.parChamp === undefined && (filtres.autres ?? []).length === 0
        ? sansFiltres(suite)
        : { ...suite, filtres };
  }

  /*
   * LES FILTRES NOMMES : leur mot, et leur RETRAIT.
   *
   * RETIRER EST DECLARATIF, ET C'EST TOUT CE QUE C'EST : le predicat reste
   * ecrit dans le code de l'app, il devient seulement inutilise. C'est
   * exactement ce qu'un client peut decider (« ce filtre ne me sert pas »)
   * sans toucher une ligne de TypeScript.
   */
  const autres = changes.filter((c) => c.quoi === 'filtreautre');
  const filtresPerdus = new Set<string>();
  if (autres.length > 0 && suite.filtres?.autres !== undefined) {
    const restants = suite.filtres.autres
      .filter((filtre) => {
        const parti = autres.some((c) => c.args[1] === filtre.id && c.args[2] === 'retirer' && c.valeur === OUI);
        if (parti) filtresPerdus.add(filtre.id);
        return !parti;
      })
      .map((filtre) => {
        const mot = autres
          .find((c) => c.args[1] === filtre.id && c.args[2] === undefined)
          ?.valeur.trim();
        return mot === undefined || mot === '' ? filtre : { ...filtre, label: mot };
      });
    if (restants.length > 0) suite = { ...suite, filtres: { ...suite.filtres, autres: restants } };
    else {
      /* UN TABLEAU VIDE EST REFUSE PAR LE SCHEMA (`min(1)` des qu'il est la) :
         la clef part avec sa derniere entree, et `filtres` avec elle si elle
         ne portait plus rien d'autre. */
      const { autres: _partis, ...sansAutres } = suite.filtres;
      suite =
        sansAutres.parChamp === undefined ? sansFiltres(suite) : { ...suite, filtres: sansAutres };
    }
  }

  /* LES FAVORIS : leurs mots, leurs retraits, et ceux qui naissent. */
  const favoris = changes.filter((c) => c.quoi === 'favori');
  const favorisNeufs = changes.filter((c) => c.quoi === 'favori-neuf');
  if (favoris.length > 0 || favorisNeufs.length > 0 || filtresPerdus.size > 0) {
    suite = favorisSuivants(suite, favoris, favorisNeufs, filtresPerdus);
  }

  /*
   * LES ACTIONS : leur mot, leur importance, et leur RETRAIT.
   *
   * RETIRER UNE ACTION EST DECLARATIF, comme pour un filtre nomme : son effet
   * (`fn`) reste dans le code de l'app, il n'est simplement plus appele. On ne
   * peut pas en CREER une ici, et pour la raison inverse : une action neuve
   * n'aurait aucun effet a nommer, donc ce serait un bouton mort.
   */
  const actions = changes.filter((c) => c.quoi === 'action');
  if (actions.length > 0 && suite.actions !== undefined) {
    const restantes = suite.actions
      .filter(
        (action) =>
          !actions.some(
            (c) => c.args[1] === action.id && c.args[2] === 'retirer' && c.valeur === OUI,
          ),
      )
      .map((action) =>
        actionSuivante(
          action,
          actions.filter((c) => c.args[1] === action.id),
        ),
      );
    suite = restantes.length > 0 ? { ...suite, actions: restantes } : sansClef(suite, 'actions');
  }

  /* LES STATS : leurs reglages, leurs retraits, et celles qui naissent. */
  const stats = changes.filter((c) => c.quoi === 'stat');
  const statsNeuves = changes.filter((c) => c.quoi === 'stat-neuve');
  if (stats.length > 0 || statsNeuves.length > 0) {
    suite = statsSuivantes(suite, stats, statsNeuves);
  }

  return suite;
}

/**
 * LES MOTS DE LA PORTE : le placeholder de sa recherche, ce qu'elle dit sur un
 * jeu VIDE et sur une fiche INTROUVABLE, ses modeles de fiches, ses plans
 * recurrents, si elle se tranche, et ce qu'ouvre un clic sur une fiche.
 *
 * UN TEXTE VIDE RETIRE SA CLEF, partout : l'absence dit deja « le socle parle »,
 * et deux ecritures pour un sens finissent par diverger.
 *
 * `tranchable` EST L'EXCEPTION, ET ELLE EST DANS LE SCHEMA : absent vaut OUI.
 * Cocher retire donc la clef, decocher ECRIT `false`. C'est l'inverse de
 * `modeles` et de `recurrences`, dont l'absence vaut NON. `navigation` suit la
 * meme regle que `tranchable`.
 */
function motsDePorte(porte: PorteDeclareeLue, changes: readonly Change[]): PorteDeclareeLue {
  let suite = porte;
  const mot = (clef: string): Change | undefined =>
    changes.find((c) => c.quoi === 'porte' && c.args[1] === clef);

  const placeholder = mot('placeholder');
  if (placeholder !== undefined) {
    suite =
      placeholder.valeur.trim() === ''
        ? sansClef(suite, 'placeholder')
        : { ...suite, placeholder: placeholder.valeur.trim() };
  }

  suite = etatSuivant(suite, changes, 'vide');
  suite = etatSuivant(suite, changes, 'introuvable');

  const modeles = mot('modeles');
  if (modeles !== undefined) {
    suite = modeles.valeur === OUI ? { ...suite, modeles: true } : sansClef(suite, 'modeles');
  }

  /* ALLUMER LES PLANS ALLUME LES MODELES (chantier `plan-sur-modele`) : un plan
     cite un modele, qui se relit et se corrige en mode Modeles, et le schema
     refuse `recurrences` sans `modeles`. Poser l'un sans l'autre ecrirait un
     fichier refuse, pour un geste que personne n'a demande de separer. */
  const recurrences = mot('recurrences');
  if (recurrences !== undefined) {
    suite =
      recurrences.valeur === OUI
        ? { ...suite, recurrences: true, modeles: true }
        : sansClef(suite, 'recurrences');
  }

  /* MODIFIER DANS LE TABLEAU : meme politique que `modeles`, le OUI seul
     s'ecrit et l'absence vaut non. La ligne n'est offerte que sur une porte qui
     monte le tableau (`motsDeLaPorte`, `projection.ts`) ; un fichier qui la
     garde sans tableau reste valide, la clef ne gouverne alors rien. */
  const modifierDansLeTableau = mot('modifierDansLeTableau');
  if (modifierDansLeTableau !== undefined) {
    suite =
      modifierDansLeTableau.valeur === OUI
        ? { ...suite, modifierDansLeTableau: true }
        : sansClef(suite, 'modifierDansLeTableau');
  }

  const tranchable = mot('tranchable');
  if (tranchable !== undefined) {
    suite =
      tranchable.valeur === OUI ? sansClef(suite, 'tranchable') : { ...suite, tranchable: false };
  }

  /* DANS LA NAVIGATION : decocher sort la porte du menu. Ni la porte
     d'arrivee ni une porte que rien ne lie ne le peuvent : ce n'est pas juge
     ici (un lien peut naitre dans le meme brouillon), le schema refuse avec
     sa raison. */
  const navigation = mot('navigation');
  if (navigation !== undefined) {
    suite =
      navigation.valeur === OUI ? sansClef(suite, 'navigation') : { ...suite, navigation: false };
  }

  /*
   * AU CLIC SUR UNE FICHE. « fiche » (ou vide) retire la clef : l'absence
   * ouvre la fiche. Un id de lien y entre ; CHANGER de lien oublie la vue,
   * qui appartenait a l'autre porte liee. Le lien n'est pas verifie ici : il
   * peut naitre dans le meme brouillon (`liaison:`), et le schema refuse
   * celui qui n'existe pas, avec sa raison.
   */
  const clic = mot('auClic');
  if (clic !== undefined) {
    const lu = clicDuBrouillon(clic.valeur);
    if (lu === null) suite = sansClef(suite, 'auClic');
    else if ('lien' in lu) {
      if (clicParLien(suite.auClic)?.lien !== lu.lien) suite = { ...suite, auClic: { lien: lu.lien } };
    } else suite = { ...suite, auClic: lu };
  }
  const vueDuClic = mot('auClicVue');
  if (vueDuClic !== undefined) {
    const vue = vueDuClic.valeur.trim();
    const parLien = clicParLien(suite.auClic);
    if (parLien !== undefined) {
      const { vue: _ancienne, ...sansVue } = parLien;
      suite = { ...suite, auClic: vue === '' ? sansVue : { ...sansVue, vue } };
    } else if (vue !== '') {
      throw new Error(
        `La vue d'arrivée du clic de la porte « ${porte.label} » demande d'abord une porte liée à ouvrir.`,
      );
    }
  }

  /*
   * « EN TÊTE DE FICHE » (chantier `fiche-suivi`) : une LISTE ordonnee
   * d'en-tetes, chacun un champ et son mode. `etapes:<n>` porte le NOM du
   * champ (`socle:status`, une clef declaree) de l'en-tete de rang `<n>`, vide
   * le retire ; `etapes:<n>:mode` dit `flux` ou `selection`. Un champ choisi
   * sans mode garde celui du rang, sinon le flux ; un mode sans champ n'a rien
   * a regler et le dit. Les anciennes clefs sans rang (`etapes`, `etapesMode`)
   * valent le rang 0. Le champ n'est pas juge ici : le schema refuse celui qui
   * n'est pas une liste du jeu, et un champ en tete deux fois.
   */
  suite = enTetesDuBrouillon(suite, changes, porte.label);
  /* LES VUES DE FICHE MONTREES, en plus de « Fiche » : la liste COMPLETE des
     cases cochees. Vide (ou `fiche`) ne garde que « Fiche » (`[]`) ; la liste
     du defaut du socle (les vues HTML du jeu, aucune vue Canvas) retire la
     clef, ce que `vuesFicheTenues` constate sur l'ecran, qui seul connait les
     vues du jeu. Une vue Canvas de fiche n'est montree que citee (27/10). */
  const vuesFiche = mot('vuesFiche');
  if (vuesFiche !== undefined) {
    suite = { ...suite, vuesFiche: liste(vuesFiche.valeur).filter((v) => v !== CLIC_FICHE && v !== 'core.fiche') };
  }
  return suite;
}

/**
 * LES EN-TETES D'UNE PORTE APRES LE BROUILLON. Les rangs visent la liste LUE
 * (`enTetesDeLaPorte` : l'ancien objet seul est le rang 0), jamais une liste
 * deja retouchee : retirer le rang 0 et changer le rang 1 dans le meme
 * brouillon touchent les deux en-tetes du fichier, sans glissement. Un rang
 * au-dela de la liste AJOUTE un en-tete, dans l'ordre des rangs. Sans aucune
 * ligne `etapes`, la porte ne bouge pas (l'ancienne forme reste telle quelle) ;
 * sinon la liste s'ecrit sous sa forme canonique, et part si elle est vide.
 */
function enTetesDuBrouillon(porte: PorteDeclareeLue, changes: readonly Change[], nom: string): PorteDeclareeLue {
  type Place = { champ: string; mode: 'flux' | 'selection' | undefined; verrous?: EnTeteDeFiche['verrous'] };
  const lignes = changes.flatMap((c) => {
    if (c.quoi !== 'porte') return [];
    const [, clef, rangBrut, sous] = c.args;
    /* LES ANCIENNES CLEFS, sans rang : le rang 0. */
    if (clef === 'etapesMode' && rangBrut === undefined) return [{ rang: 0, mode: true, valeur: c.valeur }];
    if (clef !== 'etapes') return [];
    if (rangBrut === undefined) return [{ rang: 0, mode: false, valeur: c.valeur }];
    const rang = Number(rangBrut);
    if (!Number.isInteger(rang) || rang < 0 || (sous !== undefined && sous !== 'mode')) return [];
    return [{ rang, mode: sous === 'mode', valeur: c.valeur }];
  });
  if (lignes.length === 0) return porte;
  const places = new Map<number, Place | null>(enTetesDeLaPorte(porte).map((e, rang) => [rang, { ...e }]));
  for (const ligne of lignes.filter((l) => !l.mode)) {
    const champ = ligne.valeur.trim();
    const avant = places.get(ligne.rang);
    /* LES VERROUS SUIVENT LEUR EN-TETE (`etapes-verrou`) : les lignes du Studio et de Parametres
       n'ecrivent que le champ et le mode, elles ne les perdent pas ; un AUTRE champ n'a pas
       les valeurs de l'ancien, ses verrous ne le suivent pas. */
    places.set(ligne.rang, champ === '' ? null : { champ, mode: avant?.mode, ...(avant?.champ === champ && avant.verrous !== undefined ? { verrous: avant.verrous } : {}) });
  }
  for (const ligne of lignes.filter((l) => l.mode)) {
    const voulu = ligne.valeur.trim();
    if (voulu !== 'flux' && voulu !== 'selection') {
      throw new Error(`Le mode d'« En tête de fiche » de la porte « ${nom} » est flux ou sélection, pas « ${voulu} ».`);
    }
    const place = places.get(ligne.rang);
    if (place === undefined || place === null) {
      throw new Error(`« En tête de fiche » de la porte « ${nom} » demande d'abord un champ (en-tête ${ligne.rang + 1}).`);
    }
    places.set(ligne.rang, { ...place, mode: voulu });
  }
  const enTetes = [...places.entries()]
    .sort(([a], [b]) => a - b)
    .flatMap(([, place]) => (place === null ? [] : [{ champ: place.champ, mode: place.mode ?? 'flux', ...(place.verrous === undefined ? {} : { verrou: place.verrous.length === 1 ? place.verrous[0]! : place.verrous }) }]));
  return enTetes.length === 0 ? sansClef(porte, 'etapes') : { ...porte, etapes: enTetes };
}

/**
 * Les portes que le brouillon regle et dont la liste revient au DEFAUT DU SOCLE
 * (`vuesDeFicheDeLaPorte` sans `vuesFiche` : les vues HTML du jeu, aucune vue
 * Canvas) perdent la clef ; une porte sans aucune vue de fiche aussi. « Tout
 * coche » avec une vue Canvas reste donc une liste ecrite : la clef absente ne
 * veut plus dire « toutes » (decision du fondateur, 27/10).
 */
function vuesFicheTenues(ecran: EcranLu, changes: readonly Change[]): EcranLu {
  const reglees = new Set(
    changes.filter((c) => c.quoi === 'porte' && c.args[1] === 'vuesFiche').map((c) => c.args[0] ?? ''),
  );
  if (reglees.size === 0) return ecran;
  let touche = false;
  const portes = ecran.portes.map((porte) => {
    if (!reglees.has(porte.id) || porte.vuesFiche === undefined) return porte;
    const lu = ecran as unknown as Parameters<typeof vuesDeFicheDeLaPorte>[0];
    const offertes = vuesDeFicheDeLaPorte(lu, porte.id, { sansRestriction: true });
    const sansClefLues = {
      ...lu,
      portes: lu.portes.map((p) => {
        if (p.id !== porte.id) return p;
        const { vuesFiche: _cochees, ...reste } = p;
        return reste;
      }),
    };
    const parDefaut = vuesDeFicheDeLaPorte(sansClefLues, porte.id);
    const cochees = porte.vuesFiche.filter((id) => offertes.includes(id));
    const auDefaut = cochees.length === parDefaut.length && parDefaut.every((id) => cochees.includes(id));
    if (offertes.length > 0 && !auDefaut) return porte;
    touche = true;
    return sansClef(porte, 'vuesFiche');
  });
  return touche ? { ...ecran, portes } : ecran;
}

/** La valeur de `auClic` qui ouvre la fiche, le defaut : elle retire la clef. */
export const CLIC_FICHE = 'fiche';
/** Le prefixe d'un clic qui ouvre la fiche dans une VUE DE FICHE : `vue:<vueDeFiche>`. */
export const CLIC_VUE = 'vue:';
/** Le prefixe d'un clic qui ouvre la fiche dans une AUTRE PORTE : `porte:<porte>` ou `porte:<app>/<porte>`. */
export const CLIC_PORTE = 'porte:';

type ClicDeclare = NonNullable<PorteDeclareeLue['auClic']>;

/** Le clic qui entre par un lien, ou `undefined` (vue de fiche, autre porte, fiche). */
export function clicParLien(clic: PorteDeclareeLue['auClic']): { lien: string; vue?: string } | undefined {
  return clic !== undefined && 'lien' in clic ? clic : undefined;
}

/**
 * UNE VALEUR DE BROUILLON DU CLIC, lue : `null` pour la fiche (vide ou
 * `fiche`), sinon la forme du fichier. C'est le SEUL lecteur de ces mots, pour
 * le Studio, les Parametres et les outils MCP du Studio.
 */
export function clicDuBrouillon(valeur: string): ClicDeclare | null {
  const brut = valeur.trim();
  if (brut === '' || brut === CLIC_FICHE) return null;
  if (brut.startsWith(CLIC_VUE)) {
    const vueDeFiche = brut.slice(CLIC_VUE.length).trim();
    if (vueDeFiche === '' || vueDeFiche === 'core.fiche') return null;
    return { vueDeFiche };
  }
  if (brut.startsWith(CLIC_PORTE)) {
    const cible = brut.slice(CLIC_PORTE.length).trim();
    const [app, porte] = cible.includes('/') ? cible.split('/', 2) : ['', cible];
    if ((porte ?? '') === '') throw new Error(`Le clic « ${brut} » ne nomme aucune porte.`);
    return app === '' ? { porte: porte! } : { porte: porte!, app: app! };
  }
  return { lien: brut };
}

/** La valeur de brouillon d'un clic declare : l'inverse exact de `clicDuBrouillon`. */
export function clicEnBrouillon(clic: PorteDeclareeLue['auClic']): string {
  if (clic === undefined) return CLIC_FICHE;
  if ('lien' in clic) return clic.lien;
  if ('vueDeFiche' in clic) return `${CLIC_VUE}${clic.vueDeFiche}`;
  return `${CLIC_PORTE}${clic.app === undefined ? '' : `${clic.app}/`}${clic.porte}`;
}

/**
 * LE CLIC SUR UNE FICHE QU'UN AUTRE GESTE A RENDU CADUC, elague.
 *
 * Un lien retire (avec sa porte) emporte le clic qui entrait par lui ; une vue
 * que la porte liee ne monte plus retombe sur son arrivee. Le schema refuserait
 * l'un et l'autre, et le refus parlerait d'un clic que personne n'a touche.
 * UNE PORTE DONT LE BROUILLON REGLE LE CLIC N'EST PAS ELAGUEE : ce qu'on demande
 * en toutes lettres s'ecrit, et un choix invalide revient refuse avec sa raison.
 */
function clicsElagues(ecran: EcranLu, changes: readonly Change[], avant?: EcranLu): EcranLu {
  const regles = new Set(
    changes
      .filter((c) => c.quoi === 'porte' && (c.args[1] === 'auClic' || c.args[1] === 'auClicVue'))
      .map((c) => c.args[0] ?? ''),
  );
  const vueDite = new Set(
    changes.filter((c) => c.quoi === 'porte' && c.args[1] === 'auClicVue').map((c) => c.args[0] ?? ''),
  );
  let touche = false;
  const portes = ecran.portes.map((porte) => {
    /* UN LIEN QUI VIENT D'ETRE CHOISI, sans vue dite dans le meme brouillon,
       prend la vue par defaut du clic (`vueParDefautDuClic`). */
    const parLien = clicParLien(porte.auClic);
    const lienAvant = clicParLien(avant?.portes.find((p) => p.id === porte.id)?.auClic)?.lien;
    if (parLien !== undefined && parLien.vue === undefined && !vueDite.has(porte.id) && lienAvant !== parLien.lien) {
      const lienChoisi = (porte.liens ?? []).find((l) => l.id === parLien.lien);
      const cibleChoisie = lienChoisi === undefined ? undefined : ecran.portes.find((p) => p.id === lienChoisi.porte);
      const vue = vueParDefautDuClic(cibleChoisie?.vues ?? ecran.vues);
      if (cibleChoisie !== undefined && vue !== '') {
        touche = true;
        return { ...porte, auClic: { lien: parLien.lien, vue } };
      }
    }
    if (porte.auClic === undefined || regles.has(porte.id)) return porte;
    /* UNE AUTRE PORTE DE CETTE APP QUI S'EN VA emporte le clic qui l'ouvrait,
       comme un lien retire ; une vue de fiche se juge au schema. */
    if (!('lien' in porte.auClic)) {
      const clic = porte.auClic;
      if ('porte' in clic && clic.app === undefined && !ecran.portes.some((p) => p.id === clic.porte)) {
        touche = true;
        return sansClef(porte, 'auClic');
      }
      return porte;
    }
    const lienDuClic = porte.auClic;
    const lien = (porte.liens ?? []).find((l) => l.id === lienDuClic.lien);
    const cible = lien === undefined ? undefined : ecran.portes.find((p) => p.id === lien.porte);
    if (cible === undefined) {
      touche = true;
      return sansClef(porte, 'auClic');
    }
    const vue = lienDuClic.vue;
    if (vue === undefined || (cible.vues ?? ecran.vues).includes(vue)) return porte;
    touche = true;
    return { ...porte, auClic: { lien: lienDuClic.lien } };
  });
  return touche ? { ...ecran, portes } : ecran;
}

/**
 * CE QUE LA PORTE DIT QUAND IL N'Y A RIEN A MONTRER, apres le brouillon.
 *
 * LE TITRE COMMANDE LE BLOC : le schema l'exige, donc une indication seule ne
 * s'ecrit pas. Vider le titre retire tout le bloc, et le socle reprend ses
 * phrases neutres.
 */
function etatSuivant(
  porte: PorteDeclareeLue,
  changes: readonly Change[],
  bloc: 'vide' | 'introuvable',
): PorteDeclareeLue {
  const titre = changes.find((c) => c.quoi === 'porte' && c.args[1] === `${bloc}-titre`);
  const indication = changes.find((c) => c.quoi === 'porte' && c.args[1] === `${bloc}-indication`);
  if (titre === undefined && indication === undefined) return porte;
  const courant = porte[bloc];
  const motTitre = (titre === undefined ? (courant?.titre ?? '') : titre.valeur).trim();
  if (motTitre === '') return sansClef(porte, bloc);
  const motIndication = (
    indication === undefined ? (courant?.indication ?? '') : indication.valeur
  ).trim();
  const etat: EtatDePorteLu = {
    titre: motTitre,
    ...(motIndication === '' ? {} : { indication: motIndication }),
  };
  return bloc === 'vide' ? { ...porte, vide: etat } : { ...porte, introuvable: etat };
}

/**
 * LES FAVORIS APRES LE BROUILLON : les existants, moins les retires, plus les
 * neufs, et tous prives des filtres qui n'existent plus.
 *
 * LA CASCADE EST ICI ET PAS AILLEURS : le schema refuse un favori qui pose un
 * filtre que la porte ne declare plus. Sans elle, retirer un filtre nomme
 * ferait refuser TOUTE l'ecriture, avec un message que personne ne relie au
 * geste (c'est exactement ce que fait deja `elaguerLeTri` pour le tri).
 */
function favorisSuivants(
  porte: PorteDeclareeLue,
  changes: readonly Change[],
  neufs: readonly Change[],
  filtresPerdus: ReadonlySet<string>,
): PorteDeclareeLue {
  let sortie: FavoriLu[] = (porte.favoris ?? [])
    .filter(
      (favori) =>
        !changes.some(
          (c) => c.args[1] === favori.id && c.args[2] === 'retirer' && c.valeur === OUI,
        ),
    )
    .map((favori) =>
      favoriSuivant(
        favori,
        changes.filter((c) => c.args[1] === favori.id),
      ),
    );

  const pris = new Set(sortie.map((favori) => favori.id));
  for (const uid of uidsEnAttente(neufs)) {
    const pour = neufs.filter((c) => c.args[1] === uid);
    const label = pour.find((c) => c.args[2] === 'label')?.valeur.trim() ?? '';
    /* SANS MOT, PAS DE FAVORI : le schema exige un libelle, et un preset sans
       nom ne se clique pas. L'ecran le dit sous la ligne. */
    if (label === '') continue;
    const id = idDepuisLabel(label, pris, 'favori');
    pris.add(id);
    /* AUCUN FILTRE EST UN ETAT LEGITIME (« tout le jeu ») ; la PILE DE TRI,
       elle, part vide : elle porte un SENS par niveau, que ce studio ne sait
       pas encore poser, et l'ordre du chargeur est un defaut honnete. */
    sortie.push({
      id,
      label,
      filtres: liste(pour.find((c) => c.args[2] === 'filtres')?.valeur ?? ''),
      tri: [],
    });
  }

  if (filtresPerdus.size > 0) {
    sortie = sortie.map((favori) => ({
      ...favori,
      filtres: favori.filtres.filter((filtre) => !filtresPerdus.has(filtre)),
    }));
  }

  return sortie.length > 0 ? { ...porte, favoris: sortie } : sansClef(porte, 'favoris');
}

/**
 * LES STATS APRES LE BROUILLON : les existantes, moins les retirees, plus les
 * neuves. Le PLAFOND n'est pas tenu ici : le chassis n'en rend que trois, et
 * c'est l'ecran qui grise le geste au-dela. Ce moteur ecrit ce qu'on lui
 * demande, il ne refuse pas une declaration valide.
 */
function statsSuivantes(
  porte: PorteDeclareeLue,
  changes: readonly Change[],
  neuves: readonly Change[],
): PorteDeclareeLue {
  const liste: StatLue[] = (porte.stats ?? [])
    .filter(
      (stat) =>
        !changes.some((c) => c.args[1] === stat.id && c.args[2] === 'retirer' && c.valeur === OUI),
    )
    .map((stat) =>
      statSuivante(
        stat,
        changes.filter((c) => c.args[1] === stat.id),
      ),
    );

  const pris = new Set(liste.map((stat) => stat.id));
  for (const uid of uidsEnAttente(neuves)) {
    const pour = neuves.filter((c) => c.args[1] === uid);
    const valeurDe = (clef: string): string => pour.find((c) => c.args[2] === clef)?.valeur.trim() ?? '';
    const label = valeurDe('label');
    const calcul = valeurDe('calcul');
    const champ = valeurDe('champ');
    const valeur = valeurDe('valeur');
    /*
     * CE QUE LE CALCUL EXIGE EST VERIFIE AVANT D'ECRIRE, et c'est la meme regle
     * que `statSuivante` applique aux stats existantes : « nombre de fiches »
     * ne porte aucun champ, tous les autres en exigent un, et seul « nombre
     * pour une valeur » compare a une valeur. Une stat a moitie remplie ferait
     * refuser TOUTE l'ecriture, pour tout le reste du brouillon.
     */
    if (label === '' || !CALCULS_CONNUS.has(calcul)) continue;
    if (calcul !== 'nb' && champ === '') continue;
    if (calcul === 'nbVaut' && valeur === '') continue;
    const id = idDepuisLabel(label, pris, 'stat');
    pris.add(id);
    liste.push({
      id,
      label,
      calcul,
      ...(calcul === 'nb' ? {} : { champ }),
      ...(calcul === 'nbVaut' ? { valeur } : {}),
    });
  }

  return liste.length > 0 ? { ...porte, stats: liste } : sansClef(porte, 'stats');
}

/** Les uid en attente que ces changements citent, dans l'ordre de leur arrivee. */
function uidsEnAttente(changes: readonly Change[]): string[] {
  const vus: string[] = [];
  for (const c of changes) {
    const uid = c.args[1];
    if (uid !== undefined && !vus.includes(uid)) vus.push(uid);
  }
  return vus;
}

/** La porte sans cette clef, sans que le compilateur perde sa forme. */
function sansClef<K extends keyof PorteDeclareeLue>(
  porte: PorteDeclareeLue,
  clef: K,
): PorteDeclareeLue {
  const { [clef]: _retire, ...suite } = porte;
  return suite as PorteDeclareeLue;
}

/**
 * LES PORTES APRES UN RETRAIT, ET TOUT CE QUI LES CITAIT.
 *
 * POURQUOI UNE CASCADE ET PAS UN SIMPLE `filter`. Une porte est citee ailleurs
 * dans le MEME fichier : par la porte d'arrivee de l'ecran, par les LIENS des
 * autres portes, et, a travers eux, par les cartes LIEES et les champs de TOTAL
 * du detail par vue. Le schema refuse chacune de ces citations orphelines, donc
 * un retrait nu ferait refuser TOUTE l'ecriture, avec un message qui parle d'un
 * lien que personne ne relie au geste (« le lien interventions n'existe pas »).
 *
 * LA PORTE D'ARRIVEE EST RETIREE, PAS REPOINTEE : son absence dit deja « la
 * premiere porte », qui est exactement ce qu'on veut apres avoir retire celle
 * qui accueillait. Ecrire la premiere porte restante dirait la meme chose en
 * plus fragile, et ce serait un fait ecrit deux fois.
 *
 * LA DERNIERE PORTE NE SE RETIRE PAS : `portes: []` est refuse par le schema, et
 * un ecran sans porte n'a rien a montrer. Le moteur la garde, et c'est l'ECRAN
 * qui grise le geste avec sa raison (G16) plutot que de laisser croire a une
 * ecriture qui n'arrivera pas.
 */
function portesApresRetrait(
  ecran: EcranLu,
  changes: readonly Change[],
  porteDeRemplacement: boolean = false,
): EcranLu {
  const retirees = new Set(
    changes
      .filter((c) => c.quoi === 'porte' && c.args[1] === 'retirer' && c.valeur === OUI)
      .map((c) => c.args[0] ?? ''),
  );
  if (retirees.size === 0) return ecran;
  const restantes = ecran.portes.filter((porte) => !retirees.has(porte.id));
  if (restantes.length === 0 && !porteDeRemplacement) return ecran;

  /* LES LIENS QUI VISENT UNE PORTE PARTIE, porte par porte : un id de lien
     n'a de sens que dans la porte qui le declare. */
  const liensPerdus = new Map<string, Set<string>>();
  const portes = restantes.map((porte) => {
    const perdus = (porte.liens ?? []).filter((lien) => retirees.has(lien.porte));
    if (perdus.length === 0) return porte;
    liensPerdus.set(porte.id, new Set(perdus.map((lien) => lien.id)));
    const gardes = (porte.liens ?? []).filter((lien) => !retirees.has(lien.porte));
    let sansLiens = gardes.length > 0 ? { ...porte, liens: gardes } : sansClef(porte, 'liens');
    /* LE CLIC QUI ENTRAIT PAR UN LIEN PERDU revient a la fiche. */
    if (perdus.some((lien) => lien.id === clicParLien(porte.auClic)?.lien)) sansLiens = sansClef(sansLiens, 'auClic');
    /* LE DETAIL PAR VUE DE LA PORTE, quand elle en declare un : ses cartes
       liees et ses champs de total citent SES liens, et eux seuls. */
    return sansLiens.vb === undefined
      ? sansLiens
      : { ...sansLiens, vb: vbSansLiens(sansLiens.vb, liensPerdus.get(porte.id) ?? new Set()) };
  });

  /* LE `vb` DE L'ECRAN EST HERITE par toute porte qui n'en declare pas : une
     carte liee qui y vit doit valoir pour CHACUNE d'elles, donc elle tombe des
     qu'une seule perd le lien qu'elle cite. */
  const herites = new Set<string>();
  for (const porte of portes) {
    if (porte.vb !== undefined) continue;
    for (const id of liensPerdus.get(porte.id) ?? []) herites.add(id);
  }

  const suite: EcranLu = {
    ...ecran,
    portes,
    ...(herites.size === 0 ? {} : { vb: vbSansLiens(ecran.vb, herites) }),
  };
  if (suite.porteArrivee === undefined || !retirees.has(suite.porteArrivee)) return suite;
  const { porteArrivee: _retire, ...sansArrivee } = suite;
  return sansArrivee;
}

/** Un detail par vue prive des cartes liees et des totaux qui citaient ces liens. */
function vbSansLiens(
  vb: Record<string, BindingLu | undefined>,
  perdus: ReadonlySet<string>,
): Record<string, BindingLu | undefined> {
  const suite: Record<string, BindingLu | undefined> = {};
  for (const [vue, brut] of Object.entries(vb)) {
    /* LES TOTAUX LIES PAR LIGNE suivent la meme regle que les champs de total. */
    let detail = brut;
    if (detail?.totauxLies !== undefined) {
      const gardes = detail.totauxLies.filter((total) => !perdus.has(total.liee));
      if (gardes.length !== detail.totauxLies.length) {
        const { totauxLies: _retires, ...reste } = detail;
        detail = gardes.length > 0 ? { ...reste, totauxLies: gardes } : reste;
      }
    }
    if (detail?.cartes === undefined) {
      suite[vue] = detail;
      continue;
    }
    const cartes = detail.cartes
      .filter((carte) => !(estCarteLieeLue(carte) && perdus.has(carte.liee)))
      .map((carte) => (estCarteDeChampsLue(carte) ? carteSansTotaux(carte, perdus) : carte))
      /* UNE CARTE DONT IL NE RESTE AUCUNE ENTREE est refusee par le schema :
         elle part avec le dernier total qu'elle portait. Ni la carte liee ni
         celle des fichiers joints n'ont d'entree : elles restent. */
      .filter((carte) => !estCarteDeChampsLue(carte) || carte.champs.length > 0);
    suite[vue] = cartes.length > 0 ? { ...detail, cartes } : sansCartes(detail);
  }
  return suite;
}

/** Une carte de champs privee des totaux qui comptaient les fiches d'un lien perdu. */
function carteSansTotaux(
  carte: CarteDeChampsLue,
  perdus: ReadonlySet<string>,
): CarteDeclareeLue {
  const champs = carte.champs.filter(
    (entree) => !(estTotalDeCarteLu(entree) && perdus.has(entree.liee)),
  );
  return champs.length === carte.champs.length ? carte : { ...carte, champs };
}

/** La clef du jeu qu'une porte montre (`famille::type`), ou `''` : elle n'en montre aucun. */
const clefDeJeu = (porte: PorteDeclareeLue): string =>
  porte.jeu === undefined ? '' : `${porte.jeu.family}::${porte.jeu.type ?? ''}`;

/**
 * LES PORTES NEUVES, ECRITES.
 *
 * ELLE NAIT AVEC CE QU'IL FAUT POUR ETRE MONTEE, ET RIEN DE PLUS : son id, son
 * mot, le jeu qu'elle montre, et LE DETAIL PAR VUE DE CE JEU. Pas de filtres,
 * pas de favoris, pas de stats : tout cela se regle APRES, sur la porte, quand
 * elle existe et porte un id.
 *
 * LE DETAIL PAR VUE EST COPIE D'UNE PORTE QUI MONTRE LE MEME JEU, et c'est la
 * seule partie de ce fichier qui a coute un ecran blanc en vrai. Le `vb` de
 * l'ECRAN decrit le jeu de la porte d'arrivee ; une porte neuve sur un AUTRE
 * jeu en herite, donc elle demande des colonnes qui vivent dans le jeu d'a
 * cote, et le socle JETTE au montage (« la colonne "companyId" n'est declaree
 * dans aucun jeu »). L'app entiere tombe, pas seulement la porte neuve. On
 * reprend donc le detail (et le catalogue de vues) de la porte qui montre deja
 * ce jeu : si elle n'en declare pas, c'est qu'elle herite de l'ecran, et la
 * neuve peut heriter aussi.
 *
 * SANS PORTE QUI MONTRE DEJA CE JEU, le Studio client fournit un montage
 * calcule depuis les champs declares et les natifs reels de sa famille.
 *
 * LE JEU ARRIVE EN CLEF `famille::type`, telle que le fichier de champs nomme
 * ses jeux : ce moteur ne connait pas les jeux de l'app, il recoit la clef que
 * l'ecran a montree du doigt et la decoupe. Le NOM PARLANT du jeu est le
 * libelle de la porte : le fondateur n'en tape qu'un, et le schema exige les
 * deux.
 */
function portesAjoutees(ecran: EcranLu, neuves: readonly PorteNeuve[]): PorteDeclareeLue[] {
  const pris = new Set(ecran.portes.map((porte) => porte.id));
  const sortie: PorteDeclareeLue[] = [];
  for (const neuve of neuves) {
    const [family = '', type = ''] = neuve.jeu.split('::');
    if (family === '') continue;
    const aine = ecran.portes.find((porte) => clefDeJeu(porte) === neuve.jeu);
    if (aine === undefined && neuve.montage === undefined) continue;
    const label = neuve.label.trim();
    const id = neuve.porteId ?? idDepuisLabel(label, pris, REPLI_ID_PORTE);
    pris.add(id);
    if (aine === undefined) {
      const montage = neuve.montage!;
      const champs = montage.colonnes.map((colonne) => colonne.champ);
      const titre = {
        champ: montage.titre,
        ...(montage.titre === 'socle:id' ? {} : { modifiable: true }),
      };
      sortie.push({
        id,
        label,
        vues: ['core.liste', 'core.fiche'],
        arrivee: 'core.liste',
        jeu: { nom: label, family, ...(type === '' ? {} : { type }) },
        actions: [{ id: 'ajouter', label: 'Ajouter', kind: 'add' }],
        vb: {
          'core.liste': { title: titre, ...(champs.length === 0 ? {} : { colonnes: champs }) },
          'core.fiche': {
            title: titre,
            ...(champs.length === 0 ? {} : {
              colonnes: champs,
              cartes: [{ titre: 'Informations', champs }],
            }),
          },
        },
      });
      continue;
    }
    sortie.push({
      id,
      label,
      ...(aine.vues === undefined ? {} : { vues: [...aine.vues] }),
      ...(aine.arrivee === undefined ? {} : { arrivee: aine.arrivee }),
      jeu: { nom: label, family, ...(type === '' ? {} : { type }) },
      /*
       * LES LIENS PARTENT AVEC LE DETAIL PAR VUE, et ils ne sont pas
       * separables : une carte LIEE et un champ de TOTAL citent un lien de LEUR
       * porte, et le schema refuse la citation orpheline (« la carte "Personnes"
       * cite le lien "membres", que la porte "grossistes" ne declare pas »).
       * Vu de l'app c'est la meme chose : deux portes qui montrent le meme jeu
       * ont les memes fiches liees.
       */
      ...(aine.liens === undefined ? {} : { liens: aine.liens.map((lien) => ({ ...lien })) }),
      /*
       * SON CATALOGUE DE FILTRES ET SES ACTIONS SUIVENT AUSSI, et la ligne de
       * partage est celle-ci : ce qui sert a MONTRER le jeu est repris (les
       * vues, le detail, les liens, les filtres, le bouton « + Ajouter »), ce
       * qui est un CHOIX propre a la porte ne l'est pas (ses favoris, ses
       * stats, ses mots). Sans les actions, la porte neuve serait une vitrine :
       * on ne pourrait rien y creer, alors qu'elle montre le meme jeu que sa
       * voisine. Ses favoris et ses stats, eux, sont exactement ce qu'on vient
       * declarer dessus.
       */
      ...(aine.filtres === undefined ? {} : { filtres: { ...aine.filtres } }),
      ...(aine.actions === undefined
        ? {}
        : { actions: aine.actions.map((action) => ({ ...action })) }),
      ...(aine.vb === undefined ? {} : { vb: aine.vb }),
    });
  }
  return sortie;
}

/** La porte sans son catalogue de filtres. */
function sansFiltres(porte: PorteDeclareeLue): PorteDeclareeLue {
  const { filtres: _retire, ...suite } = porte;
  return suite;
}

/** Un favori apres le brouillon : son mot et les filtres qu'il pose. */
function favoriSuivant(favori: FavoriLu, changes: readonly Change[]): FavoriLu {
  let suite = favori;
  for (const c of changes) {
    if (c.args[2] === 'label' && c.valeur.trim() !== '') {
      suite = { ...suite, label: c.valeur.trim() };
    }
    /* AUCUN FILTRE EST UN ETAT LEGITIME : « tout le parc » est un favori qui
       n'en pose aucun. Une liste vide s'ecrit donc, elle ne se retire pas. */
    if (c.args[2] === 'filtres') suite = { ...suite, filtres: liste(c.valeur) };
  }
  return suite;
}

/** Une action apres le brouillon : son mot et son importance. */
function actionSuivante(action: ActionLue, changes: readonly Change[]): ActionLue {
  let suite = action;
  for (const c of changes) {
    if (c.args[2] === 'label' && c.valeur.trim() !== '') {
      suite = { ...suite, label: c.valeur.trim() };
    }
    if (c.args[2] === 'variant') {
      /* Le defaut du socle s'ecrit en RETIRANT la clef : une action sans
         variante EST secondaire, et l'ecrire n'ajouterait rien. */
      if (c.valeur === 'primary' || c.valeur === 'secondary') {
        suite = { ...suite, variant: c.valeur };
      } else {
        const { variant: _retire, ...sansVariante } = suite;
        suite = sansVariante;
      }
    }
  }
  return suite;
}

/**
 * UNE STAT APRES LE BROUILLON.
 *
 * LE CALCUL COMMANDE CE QUI S'ECRIT : « nombre de fiches » ne porte aucun champ,
 * et seul « nombre pour une valeur » compare a une valeur. L'ecran rend les
 * trois lignes en permanence pour qu'un changement de calcul et ce qu'il demande
 * tiennent dans le MEME brouillon ; c'est ici que le surplus est ecarte, une
 * fois le calcul final connu.
 */
function statSuivante(stat: StatLue, changes: readonly Change[]): StatLue {
  let suite: StatLue = { ...stat };
  for (const c of changes) {
    if (c.args[2] === 'label' && c.valeur.trim() !== '') suite.label = c.valeur.trim();
    if (c.args[2] === 'calcul' && c.valeur.trim() !== '') suite.calcul = c.valeur.trim();
    if (c.args[2] === 'champ') suite.champ = c.valeur.trim() === '' ? undefined : c.valeur.trim();
    if (c.args[2] === 'valeur') suite.valeur = c.valeur.trim() === '' ? undefined : c.valeur.trim();
  }
  if (suite.calcul === 'nb') suite.champ = undefined;
  if (suite.calcul !== 'nbVaut') suite.valeur = undefined;
  const { champ, valeur, ...reste } = suite;
  return {
    ...reste,
    ...(champ === undefined ? {} : { champ }),
    ...(valeur === undefined ? {} : { valeur }),
  };
}

/**
 * Une colonne du catalogue apres le brouillon.
 *
 * LE NOM DE LA COLONNE SE RECOLLE, et ce n'est pas une precaution : c'est le
 * SEUL argument d'id qui puisse contenir le separateur, parce qu'un champ natif
 * s'ecrit `socle:reference`. Le lire sur `args[0]` rendait « socle », qui ne
 * correspondait a aucune colonne, et le reglage d'un total ou d'un tri sur un
 * champ natif ne s'ecrivait nulle part, en silence.
 */
function colonneSuivante(colonne: ColonneLue, changes: readonly Change[]): ColonneLue {
  let suite = colonne;
  for (const c of changes) {
    if (c.args.join(':') !== colonne.champ) continue;
    if (c.quoi === 'total') {
      /* Un agregat vide s'ecrit en RETIRANT la clef : deux facons de dire
         « aucun » produiraient deux fichiers pour un meme sens. */
      if (c.valeur === '') {
        const { total: _retire, ...sansTotal } = suite;
        suite = sansTotal;
      } else suite = { ...suite, total: c.valeur };
    }
    if (c.quoi === 'triable') {
      if (c.valeur === '') {
        const { triable: _retire, ...sansTriable } = suite;
        suite = sansTriable;
      } else suite = { ...suite, triable: c.valeur === 'oui' };
    }
  }
  return suite;
}

/** Les reglages de binding d'un ecran ou d'une porte, appliques par vue. */
function detailsSuivants(
  origine: Record<string, BindingLu | undefined>,
  changes: readonly Change[],
  cartesNeuves: readonly CarteNeuve[],
): Record<string, BindingLu | undefined> {
  const parVue = new Map<string, Change[]>();
  for (const change of changes) {
    if (!['role', 'roles', 'ecrit', 'cols', 'mot', 'carte', 'carte-neuve'].includes(change.quoi)) continue;
    const vue = change.args[0];
    if (vue === undefined) continue;
    const pourVue = parVue.get(vue);
    if (pourVue === undefined) parVue.set(vue, [change]);
    else pourVue.push(change);
  }
  for (const carte of cartesNeuves) if (!parVue.has(carte.vue)) parVue.set(carte.vue, []);
  if (parVue.size === 0) return origine;

  const vb = { ...origine };
  for (const [vue, pourVue] of parVue) {
    vb[vue] = bindingSuivant(vb[vue] ?? {}, pourVue, cartesNeuves.filter((carte) => carte.vue === vue));
  }
  return vb;
}

/** Le detail d'une vue apres le brouillon. */
function bindingSuivant(
  binding: BindingLu,
  changes: readonly Change[],
  cartesNeuves: readonly CarteNeuve[],
): BindingLu {
  let suite: BindingLu = { ...binding };

  for (const c of changes) {
    const role = c.args[1];

    if ((c.quoi === 'role' || c.quoi === 'roles') && role !== undefined) {
      suite = poserRole(suite, role, c.quoi === 'roles' ? liste(c.valeur) : origineSimple(c.valeur));
      continue;
    }
    if (c.quoi === 'ecrit' && role !== undefined) {
      suite = ecrireRole(suite, role, c.valeur === 'oui');
      continue;
    }
    if (c.quoi === 'cols') {
      const cols = liste(c.valeur);
      if (cols.length === 0) {
        const { colonnes: _retire, ...sansColonnes } = suite;
        suite = sansColonnes;
      } else suite = { ...suite, colonnes: cols };
      continue;
    }
    if (c.quoi === 'mot') {
      /* Un mot vide s'ecrit en RETIRANT la clef : la vue reprend alors celui du
         socle, ce que l'absence dit deja. Deux facons de dire « le defaut »
         produiraient deux fichiers pour un meme sens. */
      const clef = c.args[1] === 'statut' ? 'motDuStatut' : 'motDuTitre';
      if (c.valeur.trim() === '') {
        const sans = { ...suite } as Record<string, unknown>;
        delete sans[clef];
        suite = sans as BindingLu;
      } else suite = { ...suite, [clef]: c.valeur.trim() };
    }
  }

  /*
   * LES CARTES : leur contenu d'abord, leur RANG ensuite. Un rang applique
   * avant les titres renommerait la mauvaise carte. Les deplacements se font
   * ensuite un par un, chacun visant une CARTE et non un rang (voir plus bas).
   */
  const cartesChanges = changes.filter((c) => c.quoi === 'carte');
  const neuves = cartesNeuves.map(
    (c): CarteDeclareeLue => ({ titre: c.titre.trim(), champs: [...c.champs] }),
  );
  if (cartesChanges.length > 0 || neuves.length > 0) {
    const reglees = [...(suite.cartes ?? [])].map((carte, index) =>
      carteSuivante(
        carte,
        cartesChanges.filter((c) => Number(c.args[1]) === index),
      ),
    );
    /*
     * LES SECTIONS RETIREES. Le rang est la seule identite d'une carte dans le
     * fichier, donc le retrait le vise comme tous ses autres reglages. Ses
     * champs NE SE RENDENT PLUS cote app tant qu'aucune autre section ne les
     * reclame (`buildCards`, `views/core/RecordView`, regle du 22/09/2026) :
     * leur COLONNE reste declaree, c'est la section qui decide de l'affichage.
     *
     * UNE CARTE LIEE SE RETIRE PAR LE MEME ID : elle n'a ni disposition ni
     * champs a regler, mais elle a un titre et un rang, donc elle a une ligne.
     */
    const retirees = new Set(
      cartesChanges
        .filter((c) => c.args[2] === 'retirer' && c.valeur === OUI)
        .map((c) => Number(c.args[1])),
    );
    /*
     * QUI RECLAME QUOI, ET LE DERNIER GESTE GAGNE.
     *
     * LE PIEGE EST DANS LE SOCLE, ET IL EST MUET : un champ ne se rend qu'UNE
     * fois sur la fiche, la premiere carte qui le reclame le garde, et une
     * carte dont tous les champs sont deja pris n'est pas rendue DU TOUT
     * (`buildCards`). Cocher « Ville » sur une section neuve posee apres
     * « Adresse » ne donnait donc rien a voir, sans un mot. Le champ est donc
     * RETIRE des autres cartes de la meme vue : une section, et une seule.
     *
     * SEULES LES CARTES TOUCHEES RECLAMENT : celles dont le brouillon change
     * les champs, et les neuves. Un doublon deja ecrit dans le fichier n'est pas
     * defait par un brouillon qui parle d'autre chose.
     */
    const reclames = new Map<string, CarteDeclareeLue>();
    const reclamants = [
      ...reglees.filter(
        (_, index) =>
          !retirees.has(index) &&
          cartesChanges.some((c) => Number(c.args[1]) === index && c.args[2] === 'champs'),
      ),
      ...neuves,
    ];
    for (const carte of reclamants) {
      for (const nom of nomsDeChampsDeCarteLue(carte)) reclames.set(nom, carte);
    }
    let cartes = [...reglees.filter((_, index) => !retirees.has(index)), ...neuves];
    /*
     * UN DEPLACEMENT VISE UNE CARTE, PAS UN RANG, et c'est toute la difference.
     *
     * Le rang d'un changement est celui que le fondateur a LU a l'ecran, donc
     * celui d'AVANT tout deplacement. Coupe directement dans la liste, il vise
     * une carte qui a deja bouge des le second geste du meme brouillon :
     * deplacer « Notes » puis « Identification » redeplacait « Notes », et le
     * second geste defaisait le premier sans un mot, tout en comptant dans le
     * compteur du bouton Appliquer.
     *
     * On retient donc la carte VISEE par son identite, prise sur l'etat
     * d'origine, et on cherche ou elle se trouve au moment de la deplacer.
     *
     * L'ETAT D'ORIGINE GARDE LES RETIREES, et c'est ce qui fait qu'un rang
     * pose sur une carte qu'on retire dans le MEME brouillon est simplement
     * IGNORE : elle n'est plus dans la liste, `indexOf` rend -1. Les rangs des
     * autres, eux, visent toujours la carte que le fondateur a lue.
     */
    const origine = [...reglees, ...neuves];
    for (const c of cartesChanges) {
      if (c.args[2] !== 'rang') continue;
      const depuis = Number(c.args[1]);
      const vers = Number(c.valeur) - 1;
      if (!Number.isInteger(depuis) || !Number.isInteger(vers)) continue;
      if (depuis < 0 || depuis >= origine.length || vers < 0 || vers >= cartes.length) continue;
      const visee = origine[depuis];
      if (visee === undefined) continue;
      const ici = cartes.indexOf(visee);
      if (ici < 0) continue;
      const [prise] = cartes.splice(ici, 1);
      if (prise !== undefined) cartes.splice(vers, 0, prise);
    }
    /* UNE CARTE QUI N'A PLUS AUCUNE ENTREE TOMBE, et le schema l'exige de
       toute facon (`champs` est `min(1)` des qu'il est la) : elle ne serait pas
       rendue non plus, le socle ne fabrique pas de carte vide. */
    cartes = cartes
      .map((carte) => carteSansLesChampsPris(carte, reclames))
      .filter((carte) => !estCarteDeChampsLue(carte) || carte.champs.length > 0);
    suite = cartes.length === 0 ? sansCartes(suite) : { ...suite, cartes };
  }

  /* Une section ne peut citer un champ hors des colonnes de sa vue. Cocher un
     champ dans une carte le monte donc aussi dans la fiche, dans ce brouillon. */
  const demandes = [
    ...cartesChanges.filter((c) => c.args[2] === 'champs').flatMap((c) => liste(c.valeur)),
    ...cartesNeuves.flatMap((carte) => carte.champs),
  ];
  if (demandes.length > 0) {
    const colonnes = [...(suite.colonnes ?? [])];
    const deja = new Set(colonnes);
    for (const nom of demandes) {
      if (deja.has(nom)) continue;
      deja.add(nom);
      colonnes.push(nom);
    }
    suite = { ...suite, colonnes };
  }

  return suite;
}

/**
 * UNE CARTE PRIVEE DES CHAMPS QU'UNE AUTRE VIENT DE RECLAMER.
 *
 * LES TOTAUX NE SONT PAS DES CHAMPS DE CETTE FICHE, donc ils ne se reclament
 * pas : un champ de total compte les fiches LIEES, son `champ` appartient au jeu
 * d'a cote (`nomsDeChampsDeCarteLue` l'ecarte deja). Une carte qui n'a plus
 * qu'un total reste donc ecrite, et elle se rend.
 *
 * UNE CARTE LIEE NE RECLAME RIEN NON PLUS : elle n'a pas de champs. La carte
 * des fichiers joints non plus.
 */
function carteSansLesChampsPris(
  carte: CarteDeclareeLue,
  reclames: ReadonlyMap<string, CarteDeclareeLue>,
): CarteDeclareeLue {
  if (reclames.size === 0 || !estCarteDeChampsLue(carte)) return carte;
  const gardees = carte.champs.filter((entree) => {
    if (estTotalDeCarteLu(entree)) return true;
    const nom = typeof entree === 'string' ? entree : entree.champ;
    const preneuse = reclames.get(nom);
    return preneuse === undefined || preneuse === carte;
  });
  return gardees.length === carte.champs.length ? carte : { ...carte, champs: gardees };
}

/** Le binding sans son plan de cartes. */
function sansCartes(binding: BindingLu): BindingLu {
  const { cartes: _retire, ...suite } = binding;
  return suite;
}

/** Une carte apres le brouillon. */
function carteSuivante(carte: CarteDeclareeLue, changes: readonly Change[]): CarteDeclareeLue {
  let suite = carte;
  for (const c of changes) {
    if (c.args[2] === 'titre' && c.valeur.trim() !== '') suite = { ...suite, titre: c.valeur.trim() };
    /* LA VUE D'UNE CARTE LIEE (14/10/2026) : Liste ou Tableau, les deux vues que
       le schema admet dans une carte. Une autre valeur ne s'ecrit pas. */
    if (
      c.args[2] === 'vue' &&
      estCarteLieeLue(suite) &&
      (c.valeur === 'core.liste' || c.valeur === 'core.tableau')
    )
      suite = { ...suite, vue: c.valeur };
    /*
     * UNE CARTE LIEE N'A QUE SON TITRE ET SON RANG A REGLER ICI : ni
     * disposition ni champs, deux clefs que son schema refuse, et que l'ecran ne
     * lui offre donc pas (`lignesFiche`, `projectionEcrans.ts`). Le compilateur
     * le tient : `champs` n'existe pas sur cette forme. La carte des FICHIERS
     * joints est dans le meme cas : un titre et un rang, rien d'autre ici.
     */
    if (!estCarteDeChampsLue(suite)) continue;
    if (c.args[2] === 'disposition') {
      /* LE DEFAUT S'ECRIT EN RETIRANT LA CLEF : `disposition: "demi"` et
         l'absence disent la meme chose, et deux ecritures pour un sens finissent
         par diverger. Une valeur hors de l'enum ne s'ecrit pas non plus : le
         schema la refuserait au montage. */
      if (c.valeur === 'pleine' || c.valeur === 'titre-pleine') {
        /* UNE CARTE QUI PREND LA RANGEE QUITTE LA COLONNE DE DROITE : le schema
           refuse `droite` avec cette disposition. */
        const { placement: _place, ...sansPlacement } = suite;
        suite = { ...sansPlacement, disposition: c.valeur };
      } else if (c.valeur === 'demi') {
        const { disposition: _retire, ...sansDisposition } = suite;
        suite = sansDisposition;
      }
    }
    if (c.args[2] === 'placement') {
      /* LE PLACEMENT (14/10/2026), meme regle : `principal` s'ecrit en retirant
         la clef. `droite` tient en DEMI : il retire la disposition. */
      if (c.valeur === 'droite') {
        const { disposition: _retire, ...sansDisposition } = suite;
        suite = { ...sansDisposition, placement: 'droite' };
      } else if (c.valeur === 'principal') {
        const { placement: _retire, ...sansPlacement } = suite;
        suite = sansPlacement;
      }
    }
    if (c.args[2] === 'champs') {
      const champs = liste(c.valeur);
      /* Une carte sans champ serait refusee par le schema : on garde ceux
         d'avant, et la relecture le redira sur la ligne. */
      if (champs.length > 0) {
        /* LES VERROUS DEJA POSES SURVIVENT A UN RECOCHAGE. La rangee de cases
           ne coche que des NOMS : reecrire les entrees en noms nus rouvrirait
           en silence une case que quelqu'un avait fermee
           (`{ champ, modifiable: false }`). */
        const verrouilles = new Set(
          suite.champs.flatMap((entree) =>
            typeof entree !== 'string' && !estTotalDeCarteLu(entree) && entree.modifiable === false
              ? [entree.champ]
              : [],
          ),
        );
        const entrees: EntreeDeCarteLue[] = champs.map((nom) =>
          verrouilles.has(nom) ? { champ: nom, modifiable: false } : nom,
        );
        /*
         * LES CHAMPS DE TOTAL SURVIVENT AU RECOCHAGE, A LEUR RANG.
         *
         * La rangee de cases ne coche que les COLONNES de la vue, et un total
         * n'en est pas une : il ne compte aucun champ de cette fiche, il compte
         * les fiches LIEES. Reecrire la carte avec les seules cases cochees
         * l'effacerait donc du fichier au premier reglage, en silence. On le
         * repose a l'index qu'il occupait, ce qui rend la carte inchangee quand
         * rien n'a bouge.
         */
        suite.champs.forEach((entree, rang) => {
          if (!estTotalDeCarteLu(entree)) return;
          entrees.splice(Math.min(rang, entrees.length), 0, entree);
        });
        suite = { ...suite, champs: entrees };
      }
    }
  }
  return suite;
}

/** L'origine simple d'un role : aucune, un champ, ou une fonction nommee. */
function origineSimple(valeur: string): string[] | { fn: string } | null {
  const nu = valeur.trim();
  if (nu === '') return null;
  if (nu.startsWith(MARQUE_FN)) return { fn: nu.slice(MARQUE_FN.length) };
  return [nu];
}

/**
 * POSER L'ORIGINE D'UN ROLE, en gardant tout le reste de sa source.
 *
 * `vide`, `catalogue`, `habillage` et `modifiable` ne changent PAS quand on
 * change de champ : ils disent comment le role s'affiche et s'ecrit, pas d'ou
 * il vient. Les perdre ferait tomber le catalogue d'un statut au premier
 * changement de champ, et le kanban fabriquerait des colonnes fantomes.
 */
function poserRole(
  binding: BindingLu,
  role: string,
  origine: string[] | { fn: string } | null,
): BindingLu {
  const propre = !ROLES_DU_CONTRAT.has(role);
  const source = propre
    ? (binding.extra ?? {})[role]
    : (binding as Record<string, SourceLue | undefined>)[role];

  /* Aucune origine : le role disparait. Un role sans source ne serait ni un
     champ ni un calcul, donc rien du tout. */
  if (origine === null) return retirerRole(binding, role, propre);

  const { champ: _c, champs: _cs, fn: _f, ...reste } = source ?? {};
  const suivante: SourceLue =
    Array.isArray(origine)
      ? origine.length === 1
        ? { champ: origine[0]!, ...reste }
        : { champs: origine, ...reste }
      : { fn: origine.fn, ...reste };

  /* Un compose ne s'ECRIT pas : le socle ne saurait pas dans quel champ ranger
     ce qu'on tape. La clef part avec l'origine plutot que de laisser une case
     cochee qui ne fait rien. */
  if (suivante.champs !== undefined || suivante.fn !== undefined) delete suivante.modifiable;

  if (propre) return { ...binding, extra: { ...(binding.extra ?? {}), [role]: suivante } };
  return { ...binding, [role]: suivante } as BindingLu;
}

/** Le binding sans ce role. */
function retirerRole(binding: BindingLu, role: string, propre: boolean): BindingLu {
  if (!propre) {
    const suite = { ...binding } as Record<string, unknown>;
    delete suite[role];
    return suite as BindingLu;
  }
  const extra = { ...(binding.extra ?? {}) };
  delete extra[role];
  if (Object.keys(extra).length > 0) return { ...binding, extra };
  const { extra: _retire, ...sansExtra } = binding;
  return sansExtra;
}

/** Le role s'ecrit, ou ne s'ecrit plus. Sans source, il n'y a rien a marquer. */
function ecrireRole(binding: BindingLu, role: string, ecrit: boolean): BindingLu {
  const propre = !ROLES_DU_CONTRAT.has(role);
  const source = propre
    ? (binding.extra ?? {})[role]
    : (binding as Record<string, SourceLue | undefined>)[role];
  if (source === undefined) return binding;
  let suivante: SourceLue;
  if (ecrit) suivante = { ...source, modifiable: true };
  else {
    const { modifiable: _retire, ...sansModifiable } = source;
    suivante = sansModifiable;
  }
  if (propre) return { ...binding, extra: { ...(binding.extra ?? {}), [role]: suivante } };
  return { ...binding, [role]: suivante } as BindingLu;
}
